import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { tokenRequest } from '../../authConfig';
import {
  listarGrabacionesConDetalle, buscarGrabaciones, emparejar, porQueNoEmpareja,
  obtenerTranscripcion, urlDeReproduccion
} from '../../services/teamsGrabaciones';
import {
  normalizarTranscripcion, estadisticasDeHablantes, comoReloj, palabras
} from '../../services/transcripcion';
import {
  listarFormatos, listarMinutas, obtenerMinuta, guardarMinuta, editarMinuta, generarMinuta, rellenarWord, reaplicarWord,
  preguntarSobreReunion, userIdDeCuenta, borrarMinuta,
  listarCatalogo, registrarEnCatalogo, renombrarEnCatalogo, olvidarDelCatalogo
} from '../../services/minutasV2Service';
import { FUENTES, datosDeFuente, sePuedeEliminar } from './fuenteDeMinuta';
import { mismaReunion } from './emparejarMinutas';
import { datosDeLaCita } from './datosDeLaCita';
import { useAccionesReunion, RecadoDeAcciones } from './AccionesReunion';
import BarraDeMenus from './BarraDeMenus';
import ZonasDelActa from './ZonasDelActa';
import EnviarAFirmas from './EnviarAFirmas';
import { zonaDeAsistentes } from './asistentesDelActa';
import { zonasDeTareas } from './tareasDeLaMinuta';
import VistaWord from './VistaWord';
import { Leyendo } from './Ilustraciones';
import { conFolio } from './folioDeMinuta';
import { zonaDeLugar } from './lugarDelActa';
import { usePublicarActa, RecadoDePublicacion } from './PublicarActa';
import { useConfirmacion } from './Confirmacion';
import { textoDeHtml } from './descargasPosibles';
import { FUENTE, useTema } from './tema';
import { formatoPreferido } from './Configuracion';

/**
 * DetalleReunion — la pantalla de una reunión.
 *
 * Reúne en un sitio todo lo que hay sobre una reunión concreta: la grabación,
 * la transcripción, el informe generado, quién habló y cuánto, y un hilo para
 * preguntarle cosas al modelo.
 *
 * De dónde sale cada cosa:
 *   - La cita (título, asistentes, horario) → calendario de Outlook, vía Graph.
 *   - El vídeo → OneDrive del usuario, vía Graph.
 *   - La transcripción → API de streaming de SharePoint (otro token, ver
 *     teamsGrabaciones.js). No está en Graph.
 *   - El informe → motor v2, con el formato .docx que se elija.
 *
 * Las tres fuentes fallan por separado y la pantalla lo tolera: sin grabación
 * se sigue viendo la ficha; sin transcripción se avisa y las pestañas que la
 * necesitan lo dicen, en vez de quedarse en blanco.
 */

const PESTANAS = [
  { id: 'informe', etiqueta: 'Informe IA' },
  { id: 'transcripcion', etiqueta: 'Transcripción' },
  { id: 'clips', etiqueta: 'Clips' },
  { id: 'cuadro', etiqueta: 'Cuadro de mando' },
  { id: 'email', etiqueta: 'Email' },
  { id: 'preguntar', etiqueta: 'Pregunta a la IA', destacada: true }
];

const PANELES = [
  // La primera, porque es la que se mira mientras se corrige: el acta como va
  // a salir impresa, al lado de los campos que la hacen.
  { id: 'documento', etiqueta: 'Vista previa' },
  { id: 'hablantes', etiqueta: 'Hablantes' },
  { id: 'informacion', etiqueta: 'Información' },
  { id: 'comentarios', etiqueta: 'Comentarios' }
];

// Palabras que suelen marcar el momento que alguien querría volver a escuchar.
const MARCADORES = [
  { clave: 'acuerdo', patron: /\b(acordamos|acuerdo|quedamos en|de acuerdo en)\b/i, color: '#10b981' },
  { clave: 'decisión', patron: /\b(decidimos|decisión|vamos a|se aprueba|aprobado)\b/i, color: '#3b82f6' },
  { clave: 'pendiente', patron: /\b(pendiente|queda por|hay que|tenemos que|se encarga)\b/i, color: '#f59e0b' },
  { clave: 'fecha', patron: /\b(el \d{1,2} de |para el |antes del |la próxima semana|el lunes|el martes|el miércoles|el jueves|el viernes)\b/i, color: '#a78bfa' },
  { clave: 'riesgo', patron: /\b(problema|riesgo|bloquea|no funciona|preocupa|retraso)\b/i, color: '#ef4444' }
];

const estilos = (C) => ({
  // `overflow: hidden` no es cosmético. Esta pantalla es un armazón de alto
  // fijo con sus propias zonas que se desplazan por dentro; sin esto, cualquier
  // fila que se desborde un píxel —la barra al plegarse en dos líneas, por
  // ejemplo— saca una segunda barra de desplazamiento a la derecha que mueve
  // toda la aplicación. Dos desplazamientos anidados y ninguno donde se espera.
  raiz: {
    display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden',
    background: C.fondo, color: C.texto, fontFamily: FUENTE
  },

  barra: { display: 'flex', alignItems: 'center', gap: 12, padding: '14px 22px 0', flexWrap: 'wrap' },
  volver: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 30, height: 30, flexShrink: 0,
    background: 'transparent', border: `1px solid ${C.borde}`, borderRadius: 8,
    color: C.suave, cursor: 'pointer', padding: 0
  },
  titulo: { fontSize: 19, fontWeight: 600, margin: 0, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  pastilla: {
    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 11px',
    border: `1px solid ${C.borde}`, borderRadius: 8, fontSize: 12.5, color: C.suave, whiteSpace: 'nowrap'
  },
  boton: {
    display: 'inline-flex', alignItems: 'center', gap: 7, padding: '7px 13px',
    background: C.panel, border: `1px solid ${C.borde}`, borderRadius: 8,
    color: C.texto, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap'
  },
  botonAcento: { background: C.acento, borderColor: C.acento, color: '#fff' },

  // El `overflowY: hidden` no sobra, aunque nadie vaya a desplazar esto en
  // vertical: cuando un eje es `auto` y el otro se deja en `visible`, el CSS
  // convierte el segundo en `auto` también. Y la pestaña activa sobresale un
  // píxel por abajo —ese `marginBottom: -1` que la pega al borde—, así que el
  // navegador sacaba una barra de desplazamiento vertical completa, con sus
  // flechas, dentro de una franja de cincuenta píxeles de alto.
  //
  // La horizontal sí hace falta cuando las pestañas no caben, pero se esconde:
  // se desplaza con la rueda o el dedo, y una barra permanente bajo los
  // nombres los corta y ensucia la cabecera.
  pestanas: {
    display: 'flex', gap: 4, padding: '14px 22px 0',
    borderBottom: `1px solid ${C.borde}`,
    overflowX: 'auto', overflowY: 'hidden',
    scrollbarWidth: 'none', msOverflowStyle: 'none'
  },
  pestana: (activa, destacada) => ({
    padding: '9px 14px 11px', background: 'transparent', border: 'none', cursor: 'pointer',
    fontSize: 14, fontFamily: FUENTE, whiteSpace: 'nowrap',
    color: activa ? C.texto : (destacada ? C.acentoClaro : C.suave),
    fontWeight: activa ? 600 : 400,
    borderBottom: `2px solid ${activa ? C.acento : 'transparent'}`,
    marginBottom: -1
  }),

  // Las dos caras de la minuta: los campos que se editan y el documento que
  // sale. Van como pestañas y no escondidas en un menú porque son la misma
  // cosa vista de dos maneras, y alternar entre ellas es lo que se hace todo
  // el rato al revisar un acta: corriges un campo, miras cómo queda.
  // ── Lo que va al otro extremo de los menús ───────────────────────────
  //
  // Aquí vivían las palancas de vista. Se fueron cuando el documento pasó al
  // panel de la derecha: con los campos y el acta a la vista al mismo tiempo no
  // queda nada entre lo que alternar. Lo que queda son acciones.
  palancas: { display: 'inline-flex', alignItems: 'center', gap: 3 },
  // Una acción, no una palanca: no se queda hundida porque no es un estado —se
  // pulsa, pasa algo y se vuelve—.
  accionBarra: {
    display: 'inline-flex', alignItems: 'center', gap: 7,
    padding: '6px 13px', borderRadius: 7, cursor: 'pointer',
    fontSize: 13, fontFamily: FUENTE, whiteSpace: 'nowrap',
    border: `1px dashed ${C.borde}`, background: 'transparent',
    color: C.acentoClaro
  },

  cuerpo: { display: 'flex', flex: 1, minHeight: 0 },

  // ── La muesca entre las dos secciones ────────────────────────────────
  //
  // Estrecha de ver y ancha de agarrar: seis píxeles de línea, pero la zona
  // sensible es la caja entera con sus márgenes negativos. Un tirador de seis
  // píxeles se falla, y fallarlo significa seleccionar texto sin querer.
  muesca: (activa) => ({
    flex: '0 0 6px', alignSelf: 'stretch', cursor: 'col-resize',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    // Sin borde propio: el del panel de la derecha ya dibuja la línea, y dos
    // líneas a seis píxeles una de otra se ven como un error.
    background: activa ? C.acento : 'transparent',
    transition: 'background .15s'
  }),
  asaMuesca: {
    fontSize: 11, color: C.tenue, lineHeight: 1, userSelect: 'none',
    letterSpacing: -1
  },
  // Mientras se arrastra, una lámina transparente por encima de todo.
  //
  // Sin ella el arrastre se moría en cuanto el puntero entraba en el recuadro
  // de Word: un `iframe` de otro dominio se queda con los eventos del ratón y
  // esta pantalla deja de enterarse de que el puntero sigue bajado.
  lamina: {
    position: 'fixed', inset: 0, zIndex: 90, cursor: 'col-resize'
  },
  izquierda: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' },
  contenido: { flex: 1, overflowY: 'auto', padding: '20px 26px 40px' },
  // Ensanchada cuando enseña el documento: una página de Word en 340 px no se
  // lee, y verla es justo para lo que se abre esa pestaña.
  derechaAncha: { flex: '0 0 48%', maxWidth: 860 },
  derecha: {
    flex: '0 0 38%', minWidth: 340, maxWidth: 640, borderLeft: `1px solid ${C.borde}`,
    background: C.fondoHondo, display: 'flex', flexDirection: 'column', minHeight: 0
  },

  herramientas: {
    display: 'flex', alignItems: 'center', gap: 10, padding: '12px 26px',
    borderBottom: `1px solid ${C.borde}`, flexWrap: 'wrap'
  },
  select: {
    padding: '7px 11px', borderRadius: 8, fontSize: 13, fontFamily: FUENTE,
    background: C.panelAlto, color: C.texto, border: `1px solid ${C.borde}`, maxWidth: 280
  },
  iconoBoton: {
    background: 'transparent', border: 'none', color: C.suave, cursor: 'pointer',
    fontSize: 15, padding: '5px 7px', borderRadius: 6, lineHeight: 1
  },

  video: { width: '100%', aspectRatio: '16 / 9', background: '#000', display: 'block' },
  barraVideo: {
    display: 'flex', alignItems: 'center', gap: 8, width: '100%',
    padding: '7px 14px', background: 'none', border: 'none',
    borderBottom: `1px solid ${C.borde}`, cursor: 'pointer',
    fontFamily: FUENTE, fontSize: 12.5, fontWeight: 600, color: C.suave
  },
  flechaVideo: (plegado) => ({
    fontSize: 11, color: C.tenue, transition: 'transform .15s',
    transform: plegado ? 'rotate(-90deg)' : 'none'
  }),
  pistaVideo: { fontSize: 11.5, fontWeight: 500, color: C.tenue },
  videoVacio: {
    width: '100%', aspectRatio: '16 / 9', background: '#000', display: 'flex',
    flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8,
    color: C.tenue, fontSize: 13.5, textAlign: 'center', padding: 16, boxSizing: 'border-box'
  },

  tarjetaHablante: { background: C.panel, border: `1px solid ${C.borde}`, borderRadius: 11, padding: '13px 15px', marginBottom: 11 },
  avatar: (color) => ({
    width: 30, height: 30, borderRadius: '50%', background: color, flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 600, color: '#fff'
  }),
  linea: { position: 'relative', height: 8, background: C.panelAlto, borderRadius: 4, marginTop: 12, overflow: 'hidden' },

  turno: { display: 'flex', gap: 12, padding: '11px 0', borderBottom: `1px solid ${C.panelAlto}` },
  sello: {
    flexShrink: 0, fontSize: 12, color: C.acentoClaro, fontVariantNumeric: 'tabular-nums',
    background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, paddingTop: 2, fontFamily: FUENTE
  },

  metrica: { background: C.panel, border: `1px solid ${C.borde}`, borderRadius: 11, padding: '15px 17px' },
  metricaValor: { fontSize: 25, fontWeight: 600, fontVariantNumeric: 'tabular-nums' },
  metricaEtiqueta: { fontSize: 12.5, color: C.suave, marginTop: 3 },

  campoTexto: {
    width: '100%', boxSizing: 'border-box', padding: '11px 13px', borderRadius: 9,
    background: C.panel, color: C.texto, border: `1px solid ${C.borde}`,
    fontSize: 13.5, fontFamily: FUENTE, lineHeight: 1.6, resize: 'vertical'
  },
  burbuja: (mia) => ({
    maxWidth: '85%', padding: '11px 14px', borderRadius: 12, fontSize: 13.5, lineHeight: 1.6,
    whiteSpace: 'pre-wrap', alignSelf: mia ? 'flex-end' : 'flex-start',
    background: mia ? C.acento : C.panel,
    border: `1px solid ${mia ? C.acento : C.borde}`,
    color: mia ? '#fff' : C.texto
  }),

  vacio: { color: C.suave, fontSize: 14, lineHeight: 1.65, maxWidth: 560 },

  // El sitio donde iba a estar el Word, cuando no hay Word que poner. Centrado
  // y ancho: es lo que la persona vino a buscar y tiene que encontrarlo sin
  // leer la pantalla entera.
  llamada: {
    textAlign: 'center', padding: '26px 24px', marginBottom: 16,
    border: `1px dashed ${C.borde}`, borderRadius: 10, background: C.panel,
    color: C.suave, fontSize: 14, lineHeight: 1.65
  }
});

const COLORES_AVATAR = ['#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#06b6d4', '#ef4444'];
const colorDe = (nombre) => {
  let suma = 0;
  for (let i = 0; i < nombre.length; i++) suma += nombre.charCodeAt(i);
  return COLORES_AVATAR[suma % COLORES_AVATAR.length];
};

/**
 * El registro de lo que se intentó — en la consola, no en la pantalla.
 *
 * Esto fue un desplegable («Qué se intentó, 14 pasos») y no debía serlo: la
 * búsqueda de la grabación recorre OneDrive, SharePoint y el calendario, y
 * contarlo paso a paso es ruido para quien solo venía a leer su acta.
 *
 * Tampoco se borra. Cuando una reunión aparece sin vídeo, esos pasos son la
 * única respuesta a "¿y por qué?". Van a la consola del navegador: ahí los lee
 * quien los necesita y no estorban a quien no.
 */
const registrar = (texto) => {
  // eslint-disable-next-line no-console
  console.log('[minutas] %s', texto);
};

/** Estilos del informe: el HTML lo escribe el modelo, aquí solo se presenta. */
/**
 * El acta se pinta como lo que es: un documento en papel.
 *
 * Antes heredaba los colores del tema, y en oscuro había celdas ilegibles. La
 * causa: el .docx trae su propio sombreado —tu plantilla pinta de gris claro
 * las celdas de etiqueta, «Fecha:», «Convocado por:»— y esa orden viaja en el
 * atributo `style` de cada celda, que gana a cualquier hoja de estilos. Quedaba
 * texto claro sobre fondo claro.
 *
 * Forzar el fondo de las celdas lo habría tapado, pero a costa de borrar una
 * distinción que la plantilla hace a propósito, y sin resolver el problema de
 * fondo: un .docx puede traer además texto de color, resaltados o bordes, y
 * cada uno habría vuelto a romperse sobre oscuro.
 *
 * Sobre papel blanco, en cambio, todo lo que traiga el documento se ve como se
 * ve en Word. Es además lo que ya hace la exportación a PDF, así que la
 * pantalla y el archivo se parecen en vez de discrepar. Un acta no es una
 * pantalla: es un documento que alguien va a imprimir o mandar.
 */
const estiloInforme = () => `
.minuta-v2 {
  background: #ffffff; color: #111827;
  max-width: 860px; padding: 26px 30px;
  border: 1px solid #d1d5db; border-radius: 8px;
  font-size: 14.5px; line-height: 1.75;
}
.minuta-v2 h1 { font-size: 21px; margin: 22px 0 12px; font-weight: 600; color: #111827; }
.minuta-v2 h2 { font-size: 18px; margin: 24px 0 11px; font-weight: 600; color: #111827; }
.minuta-v2 h3 { font-size: 15.5px; margin: 20px 0 9px; font-weight: 600; color: #374151; }
.minuta-v2 :first-child { margin-top: 0; }
.minuta-v2 p { margin: 0 0 11px; }
.minuta-v2 ul, .minuta-v2 ol { margin: 0 0 13px; padding-left: 24px; }
.minuta-v2 li { margin-bottom: 6px; }
.minuta-v2 strong { color: #000000; font-weight: 600; }
.minuta-v2 table { border-collapse: collapse; width: 100%; margin: 14px 0; font-size: 13.5px; }
.minuta-v2 th, .minuta-v2 td { border: 1px solid #d1d5db; padding: 8px 11px; text-align: left; vertical-align: top; }
.minuta-v2 th { background: #f3f4f6; font-weight: 600; }
.minuta-v2 img { max-width: 100%; }
.minuta-v2 a { color: #1d4ed8; }
`;

const DetalleReunion = ({ reunion = null, minuteId = null, grabacion: grabacionInicial = null, onVolver, onGenerarConFormato }) => {
  const { instance, accounts } = useMsal();
  const cuenta = accounts && accounts[0];
  // La direccion y el nombre de quien ha entrado. `username` es el UPN en
  // Microsoft Entra, que salvo excepciones es su correo; el `userId` NO sirve
  // para escribirle: es un identificador de cuenta.
  const miCorreo = (cuenta && (cuenta.username || (cuenta.idTokenClaims
    && (cuenta.idTokenClaims.preferred_username || cuenta.idTokenClaims.email)))) || '';
  const miNombre = (cuenta && (cuenta.name || (cuenta.idTokenClaims
    && cuenta.idTokenClaims.name))) || '';
  const userId = userIdDeCuenta(cuenta);

  const { C, aviso } = useTema();
  const S = useMemo(() => estilos(C), [C]);

  const [pestana, setPestana] = useState('informe');
  const [panel, setPanel] = useState('documento');

  // ── El ancho del panel derecho, a gusto de cada quien ─────────────────
  //
  // `null` significa «el de por sí»: ancho cuando enseña el documento, más
  // estrecho para lo demás. En cuanto alguien arrastra la muesca, manda su
  // medida y deja de moverse sola — cambiar de pestaña no puede deshacer lo que
  // alguien acaba de ajustar a mano.
  //
  // Se recuerda en el navegador porque es una preferencia de quien mira, no del
  // acta: quien trabaja en una pantalla ancha lo pone ancho y no quiere
  // repetirlo en cada reunión. Y se lee dentro de un `try`: en una ventana
  // privada o con el almacenamiento bloqueado, leerlo lanza.
  const LLAVE_ANCHO = 'minutas-v2-ancho-panel';
  const LLAVE_VIDEO = 'minutas-v2-video-plegado';
  const MIN_PANEL = 280;
  const MIN_IZQUIERDA = 420;

  const [anchoPanel, setAnchoPanel] = useState(() => {
    try {
      const guardado = Number(window.localStorage.getItem(LLAVE_ANCHO));
      return guardado >= MIN_PANEL ? guardado : null;
    } catch { return null; }
  });
  const [panelPlegado, setPanelPlegado] = useState(false);
  const [ajustando, setAjustando] = useState(false);

  // ── El vídeo, plegado o no ────────────────────────────────────────────
  //
  // El reproductor se lleva un 16:9 entero de la columna, y debajo va el acta.
  // Quien está corrigiendo el documento y no va a volver a ver la grabación
  // quiere esa altura para el acta, no para un fotograma congelado.
  //
  // La elección se recuerda, como el ancho del panel: volver a plegarlo en cada
  // acta sería pedir lo mismo una vez por reunión.
  const [videoPlegado, setVideoPlegado] = useState(() => {
    try { return window.localStorage.getItem(LLAVE_VIDEO) === '1'; } catch { return false; }
  });

  useEffect(() => {
    try { window.localStorage.setItem(LLAVE_VIDEO, videoPlegado ? '1' : '0'); } catch { /* da igual */ }
  }, [videoPlegado]);

  const cuerpoRef = useRef(null);

  const anotarAncho = useCallback((px) => {
    setAnchoPanel(px);
    try { window.localStorage.setItem(LLAVE_ANCHO, String(px)); } catch { /* da igual */ }
  }, []);

  /** El ancho que resulta de tener el puntero aquí. */
  const medirDesde = useCallback((clientX) => {
    const caja = cuerpoRef.current && cuerpoRef.current.getBoundingClientRect();
    if (!caja) return null;
    const crudo = Math.round(caja.right - clientX);
    // Ni tan estrecho que no se lea, ni tan ancho que el acta no quepa: las dos
    // secciones tienen un mínimo y el arrastre se para ahí.
    return Math.max(MIN_PANEL, Math.min(crudo, Math.round(caja.width) - MIN_IZQUIERDA));
  }, []);

  useEffect(() => {
    if (!ajustando) return undefined;
    const mover = (e) => {
      const px = medirDesde(e.clientX);
      if (px != null) setAnchoPanel(px);
    };
    const soltar = (e) => {
      const px = medirDesde(e.clientX);
      if (px != null) anotarAncho(px);
      setAjustando(false);
    };
    window.addEventListener('mousemove', mover);
    window.addEventListener('mouseup', soltar);
    return () => {
      window.removeEventListener('mousemove', mover);
      window.removeEventListener('mouseup', soltar);
    };
  }, [ajustando, medirDesde, anotarAncho]);

  const [minuta, setMinuta] = useState(null);
  const [crudaTranscripcion, setCrudaTranscripcion] = useState(null);

  // El .docx ya generado en esta visita. Se conserva para que descargarlo sea
  // entregar exactamente lo que se está viendo, sin una segunda llamada.
  const [docxListo, setDocxListo] = useState(null);
  const [grabacion, setGrabacion] = useState(grabacionInicial);
  const [formatos, setFormatos] = useState([]);
  const [formatoSel, setFormatoSel] = useState('');

  // Los lugares que esta persona ya ha usado, para ofrecerlos al escribir el
  // del acta. Se piden una vez al entrar: son una lista corta y no cambian
  // mientras se revisa una minuta.
  const [lugares, setLugares] = useState([]);

  const [cargandoMedios, setCargandoMedios] = useState(true);
  const [errorMedios, setErrorMedios] = useState(null);
  const [urlVideo, setUrlVideo] = useState(null);
  // Qué está haciendo el reproductor. Un vídeo de Teams puede pesar cientos de
  // megas: sin esto, "cargando" para siempre es indistinguible de "roto".
  const [estadoVideo, setEstadoVideo] = useState('inactivo');
  // Por qué falló, en palabras. El navegador sí lo sabe (video.error.code) pero
  // hasta ahora se tiraba, y un recuadro negro no distingue 'enlace caducado'
  // de 'códec no soportado' ni de 'la CSP lo bloqueó'.
  const [motivoVideo, setMotivoVideo] = useState(null);
  // Un enlace de Graph caduca en cosa de una hora. Si falla, se pide otro una
  // sola vez: reintentar en bucle con un enlace igual de muerto no arregla nada.
  const [reintentoVideo, setReintentoVideo] = useState(false);
  const [guardandoZonas, setGuardandoZonas] = useState(false);
  const [errorZonas, setErrorZonas] = useState(null);
  // Ya no hay palancas de vista. Abrir una reunión con acta es, casi siempre,
  // ir a tocarla, así que el editor de campos es lo que se ve; el documento
  // está al lado, en el panel de la derecha, y no hay nada entre lo que
  // alternar.

  // Dónde vive el acta subida, cuando la subida está al día. Lo dice la vista de
  // Word, que es quien la sube; lo usa exportar a PDF, que necesita un documento
  // que convertir y no un texto que fotografiar.
  const [documentoEnLinea, setDocumentoEnLinea] = useState(null);
  const [firmas, setFirmas] = useState(false);

  // Los avisos de «esto no se deshace» se pintan dentro de la aplicación, no
  // con el cuadro del navegador.
  const [dialogo, confirmar] = useConfirmacion();
  const [generando, setGenerando] = useState(false);
  const [errorGenerar, setErrorGenerar] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [copiado, setCopiado] = useState(false);

  const [busqueda, setBusqueda] = useState('');
  const [expandido, setExpandido] = useState({});
  const [hilo, setHilo] = useState([]);
  const [pregunta, setPregunta] = useState('');
  const [preguntando, setPreguntando] = useState(false);
  const [correoBorrador, setCorreoBorrador] = useState('');
  const [redactando, setRedactando] = useState(false);
  const [comentarios, setComentarios] = useState([]);
  const [comentarioNuevo, setComentarioNuevo] = useState('');

  const videoRef = useRef(null);

  /**
   * Lo último que bloqueó la política de seguridad de contenido.
   *
   * Cuando la CSP corta un vídeo, el elemento `<video>` solo sabe decir
   * `MEDIA_ERR_SRC_NOT_SUPPORTED`, que es el mismo código que da un enlace
   * caducado o un archivo que no es un vídeo. Ahí se acaba lo que puede contar
   * el reproductor, y adivinar entre esos tres casos lleva a dar por caducado
   * lo que no lo está. El navegador sí lo sabe, y lo cuenta en este evento con
   * la directiva y el origen exactos.
   *
   * Importa sobre todo porque las URLs de descarga de OneDrive redirigen a
   * otros dominios (`*.svc.ms`, `*.files.1drv.com`) y la CSP se aplica también
   * al destino del redirect: el host que se permitió no es el que se bloquea.
   */
  const bloqueoCsp = useRef(null);

  useEffect(() => {
    const anotar = (e) => {
      bloqueoCsp.current = {
        uri: e.blockedURI || '',
        directiva: e.effectiveDirective || e.violatedDirective || '',
        cuando: Date.now()
      };
    };
    document.addEventListener('securitypolicyviolation', anotar);
    return () => document.removeEventListener('securitypolicyviolation', anotar);
  }, []);

  /**
   * Rehace el .docx de una minuta que ya estaba guardada.
   *
   * Lo que se guarda en la base de datos es el HTML del acta, no el documento.
   * Sin esto, al reabrir una reunión aparecía el informe pero no el .docx, y con
   * él desaparecían «Corregir a mano» y «Ver como Word»: las dos solo existían
   * en la sesión en la que se generó el acta, que es tanto como no tenerlas.
   *
   * Se rearma a partir del contenido por zona que viaja con la minuta. No pasa
   * por el modelo —es la misma ruta que usa guardar una corrección—, así que no
   * cuesta tokens ni devuelve un acta distinta de la guardada.
   */
  const [rearmando, setRearmando] = useState(false);

  // Lo que el editor de campos deja saber de sí mismo: si hay algo que
  // deshacer, y si está guardando. Vive aquí porque deshacer se ofrece desde el
  // menú, que lo pinta esta pantalla y no el panel.
  const [edicion, setEdicion] = useState(null);

  useEffect(() => {
    if (!minuta || !userId || docxListo) return undefined;

    const guardado = minuta.engineMeta;
    if (!minuta.formatId || !guardado || !guardado.valores) return undefined;

    let vigente = true;
    setRearmando(true);
    // Tambien al abrir, no solo al guardar: si no, el acta se veria sin las
    // firmas hasta que alguien corrigiera algo, que es justo al reves de lo
    // que se espera de un documento ya firmado.
    reaplicarWord({
      formatId: minuta.formatId, valores: guardado.valores, userId,
      minuteId: minuta.minuteId || null
    })
      .then(r => { if (vigente) setDocxListo({ ...r, formatId: minuta.formatId }); })
      .catch(() => {
        // Sin documento se sigue viendo el informe guardado; lo único que se
        // pierde es poder corregirlo o verlo como Word, y decirlo aquí sería
        // alarmar por algo que la mayoría no iba a usar en esta visita.
      })
      .finally(() => { if (vigente) setRearmando(false); });

    return () => { vigente = false; };
  }, [minuta, userId, docxListo]);

  /**
   * ¿Esta acta puede llegar a verse en Word?
   *
   * Hace falta la plantilla con la que se armó y el contenido por zona. Las
   * actas guardadas antes de que existiera esto —o regeneradas mientras el
   * formato no se guardaba— tienen el informe pero no eso, y no hay manera de
   * deducirlo: el HTML no dice sobre qué documento se escribió. Se distingue
   * para poder ofrecer generarla de nuevo en vez de dejar la pantalla como si
   * el Word estuviera a punto de aparecer.
   */
  const puedeVerseEnWord = !!(
    docxListo
    || rearmando
    || (minuta && minuta.formatId && minuta.engineMeta && minuta.engineMeta.valores)
  );

  // Aquí se reponían las palancas de vista al cambiar de acta. Ya no hay
  // palancas: el editor de campos es siempre lo que se ve, y el documento está
  // al lado. La pestaña del panel derecho se queda donde la dejó cada quien,
  // que es como se comporta cualquier pestaña.

  const claveLocal =`minutas-v2-comentarios-${minuteId || (reunion && reunion.id) || 'suelta'}`;

  // ── Carga ──────────────────────────────────────────────────

  const tokenGraph = useCallback(async () => {
    const r = await instance.acquireTokenSilent({ ...tokenRequest, account: cuenta });
    return r.accessToken;
  }, [instance, cuenta]);

  /**
   * Token de SharePoint para el host donde vive el vídeo.
   *
   * El host importa: `contoso-my.sharepoint.com` (los OneDrive personales) y
   * `contoso.sharepoint.com` (los sitios de equipo) son recursos distintos y
   * cada uno quiere su token. Antes se pedía siempre para el mismo dominio
   * fijo, lo que solo servía si la grabación era propia.
   */
  const tokenSharePoint = useCallback(async (sitio) => {
    const host = sitio ? new URL(sitio).host : process.env.REACT_APP_SHAREPOINT_DOMAIN;
    const peticion = { scopes: [`https://${host}/.default`], account: cuenta };

    try {
      const r = await instance.acquireTokenSilent(peticion);
      return r.accessToken;
    } catch {
      // Es otro recurso: el consentimiento silencioso puede no estar dado
      // aunque la sesión de Graph sí lo esté.
      const r = await instance.acquireTokenPopup(peticion);
      return r.accessToken;
    }
  }, [instance, cuenta]);

  /**
   * Deja lista una grabación: su vídeo y su transcripción.
   *
   * Se usa tanto desde el emparejamiento automático como cuando la persona
   * elige otra a mano, para que ambas rutas hagan exactamente lo mismo.
   */
  const cargarGrabacion = useCallback(async (elegida, anotar = () => {}) => {
    setGrabacion(elegida);
    setUrlVideo(null);
    setCrudaTranscripcion(null);
    setErrorMedios(null);
    setMotivoVideo(null);
    setReintentoVideo(false);

    // Las dos cosas a la vez, y cada una se pinta en cuanto llega. En serie,
    // la transcripción —que es lo pesado— esperaba a que resolviera el vídeo
    // sin ninguna razón, porque no dependen entre sí.
    const video = (async () => {
      if (elegida.urlDescarga) {
        setUrlVideo(elegida.urlDescarga);
        anotar('Vídeo listo para reproducir');
        return;
      }
      try {
        const fresca = await urlDeReproduccion(await tokenGraph(), elegida);
        if (fresca) {
          setUrlVideo(fresca);
          anotar('Vídeo listo (URL renovada)');
        } else {
          anotar('Graph no devolvió una URL de reproducción para este archivo');
        }
      } catch (e) {
        anotar(`No se pudo obtener la URL del vídeo: ${e.message}`);
      }
    })();

    // Aparte a propósito: que falle la transcripción no debe dejar sin vídeo.
    const transcripcion = (async () => {
      try {
        const token = await tokenSharePoint(elegida.sitio);
        const datos = await obtenerTranscripcion(token, elegida);
        setCrudaTranscripcion(datos);
        anotar(`Transcripción descargada de ${elegida.sitio || 'SharePoint'}`);
      } catch (e) {
        anotar(`Transcripción: ${e.mensajeUsuario || e.message}`);
        setErrorMedios(e.mensajeUsuario || e.message);
      }
    })();

    await Promise.all([video, transcripcion]);
  }, [tokenGraph, tokenSharePoint]);

  // La minuta guardada, si se abrió desde una tarjeta de minuta.
  useEffect(() => {
    if (minuteId == null || !userId) return;
    let vigente = true;

    obtenerMinuta(minuteId, userId)
      .then(m => {
        if (!vigente) return;
        setMinuta(m);
        if (m.transcript) setCrudaTranscripcion(m.transcript);
      })
      .catch(e => vigente && setErrorGenerar(e.mensajeUsuario || e.message));

    return () => { vigente = false; };
  }, [minuteId, userId]);

  // Si esta reunión ya tiene minuta, se abre esa. Sin esta comprobación, cada
  // visita ofrecía generar de nuevo y acababan varias minutas de la misma
  // reunión compitiendo en el listado.
  useEffect(() => {
    if (minuteId != null || !userId || !reunion) return;
    let vigente = true;

    listarMinutas(userId)
      .then(todas => {
        if (!vigente) return;
        const suya = todas.find(m => mismaReunion(m, reunion));
        if (!suya) return;
        return obtenerMinuta(suya.minuteId, userId).then(completa => {
          if (!vigente) return;
          setMinuta(completa);
          if (completa.transcript && !crudaTranscripcion) setCrudaTranscripcion(completa.transcript);
        });
      })
      .catch(() => { /* si no se puede consultar, se ofrece generar */ });

    return () => { vigente = false; };
    // `crudaTranscripcion` queda fuera a propósito: esto solo debe correr al
    // abrir la reunión, no cada vez que llega la transcripción de SharePoint.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minuteId, userId, reunion]);

  // La grabación y su transcripción, si se abrió desde una cita del calendario.
  useEffect(() => {
    if (!cuenta || !reunion) { setCargandoMedios(false); return; }
    let vigente = true;

    (async () => {
      setCargandoMedios(true);
      setErrorMedios(null);
      const anotar = registrar;

      try {
        // ── 1. La grabación ──
        // Si la pantalla anterior ya la localizó, no se vuelve a recorrer
        // OneDrive entero: son varias llamadas a Graph por cada apertura.
        let encontrada = grabacionInicial;

        const token = await tokenGraph();

        if (encontrada) {
          // Ya sabemos cuál es: se carga de inmediato. El listado completo se
          // trae después, en segundo plano, solo para poder ofrecer el selector
          // si resulta que no era esta. Esperarlo aquí era hacer aguardar al
          // usuario por algo que quizá no llegue a usar.
          anotar(`Grabación recibida del listado: "${encontrada.titulo}"`);
          setCargandoMedios(false);
          cargarGrabacion(encontrada, anotar);

          return;
        }

        {
          const { grabaciones, diagnostico } = await listarGrabacionesConDetalle(token, { dias: 90 });
          if (!vigente) return;
          diagnostico.forEach(anotar);
          encontrada = emparejar(grabaciones, reunion);

          // Última bala: buscar el asunto en todo lo que el usuario puede ver.
          // Es lo único que alcanza las reuniones de canal, cuyo vídeo va a la
          // biblioteca del canal y no a ningún OneDrive personal.
          if (!encontrada && reunion && reunion.titulo) {
            try {
              const halladas = await buscarGrabaciones(token, reunion.titulo);
              anotar(`Búsqueda en SharePoint por "${reunion.titulo}": ${halladas.length} resultados`);
              if (halladas.length > 0) {
                encontrada = emparejar(halladas, reunion) || null;
                if (encontrada) anotar(`Encontrada por búsqueda: "${encontrada.titulo}"`);
              }
            } catch (e) {
              anotar(`La búsqueda en SharePoint falló: ${e.mensajeUsuario || e.message}`);
            }
          }

          if (!encontrada) {
            anotar(porQueNoEmpareja(grabaciones, reunion));
            return;
          }
          anotar(`Emparejada con "${encontrada.titulo}" — ${encontrada.motivo}`);
        }

        if (!vigente) return;
        await cargarGrabacion(encontrada, anotar);
      } catch (e) {
        if (vigente) {
          anotar(`Error: ${e.mensajeUsuario || e.message}`);
          setErrorMedios(e.mensajeUsuario || e.message);
        }
      } finally {
        if (vigente) setCargandoMedios(false);
      }
    })();

    return () => { vigente = false; };
  }, [cuenta, reunion, grabacionInicial, tokenGraph, cargarGrabacion]);

  useEffect(() => {
    if (!userId) return;
    let vigente = true;
    listarFormatos(userId)
      .then(f => {
        if (!vigente) return;
        setFormatos(f);
        // El que la persona marcó por defecto en Configuración manda sobre el
        // primero de la lista: si eligió uno, es el que espera ver aquí.
        const guardado = formatoPreferido(userId);
        const existe = guardado && f.some(x => String(x.formatId) === String(guardado));
        if (existe) setFormatoSel(String(guardado));
        else if (f.length > 0) setFormatoSel(String(f[0].formatId));
      })
      .catch(() => vigente && setFormatos([]));
    return () => { vigente = false; };
  }, [userId]);

  useEffect(() => {
    try {
      setComentarios(JSON.parse(window.localStorage.getItem(claveLocal) || '[]'));
    } catch {
      setComentarios([]);
    }
  }, [claveLocal]);

  // ── Derivados ──────────────────────────────────────────────

  const trans = useMemo(() => normalizarTranscripcion(crudaTranscripcion), [crudaTranscripcion]);

  const duracion = useMemo(() => {
    if (grabacion && grabacion.duracion) return grabacion.duracion;
    if (trans.duracion) return trans.duracion;
    if (reunion && reunion.inicio && reunion.fin) return (reunion.fin - reunion.inicio) / 1000;
    return null;
  }, [grabacion, trans.duracion, reunion]);

  const hablantes = useMemo(
    () => estadisticasDeHablantes(trans.turnos, duracion),
    [trans.turnos, duracion]
  );

  const turnosVisibles = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    if (!q) return trans.turnos;
    return trans.turnos.filter(t =>
      t.texto.toLowerCase().includes(q) || (t.hablante || '').toLowerCase().includes(q)
    );
  }, [trans.turnos, busqueda]);

  // Momentos: turnos que contienen una de las expresiones que suelen marcar algo
  // que conviene volver a oír. No es un modelo, es una búsqueda: por eso cada
  // momento dice qué palabra lo hizo saltar.
  const momentos = useMemo(() => {
    const encontrados = [];
    for (const turno of trans.turnos) {
      for (const marcador of MARCADORES) {
        if (marcador.patron.test(turno.texto)) {
          encontrados.push({ turno, tipo: marcador.clave, color: marcador.color });
          break;
        }
      }
    }
    return encontrados;
  }, [trans.turnos]);

  const totalPalabras = useMemo(
    () => trans.turnos.reduce((s, t) => s + palabras(t.texto), 0),
    [trans.turnos]
  );

  const titulo = (minuta && minuta.title) || (reunion && reunion.titulo) || 'Reunión';
  const hayTranscripcion = trans.turnos.length > 0 || Boolean(trans.texto);

  // ── Acciones ───────────────────────────────────────────────

  /**
   * Vuelve a pedir la URL del vídeo.
   *
   * La que da OneDrive va firmada y caduca en cosa de una hora. Si la pestaña
   * lleva rato abierta, el reproductor se queda cargando sin decir por qué;
   * esto lo arregla sin tener que recargar la página.
   */
  const renovarVideo = useCallback(async () => {
    if (!grabacion) return;
    setEstadoVideo('cargando');
    setMotivoVideo(null);
    try {
      const fresca = await urlDeReproduccion(await tokenGraph(), grabacion);
      if (fresca) {
        setUrlVideo(fresca);
        registrar('Enlace del vídeo renovado');
      } else {
        setEstadoVideo('error');
        setMotivoVideo('Graph no devolvió ninguna URL de reproducción para este archivo.');
      }
    } catch (e) {
      registrar(`No se pudo renovar el enlace: ${e.message}`);
      setEstadoVideo('error');
      setMotivoVideo(`No se pudo pedir un enlace nuevo: ${e.message}`);
    }
  }, [grabacion, tokenGraph]);

  /**
   * Qué ha pasado con el vídeo, según el propio navegador.
   *
   * `MediaError.code` es lo único que distingue de verdad los casos, y los tres
   * que salen aquí piden remedios distintos:
   *
   *   - NETWORK  → la descarga se cortó. Reintentar suele bastar.
   *   - DECODE   → el archivo llegó pero no se puede decodificar.
   *   - SRC_NOT_SUPPORTED → el navegador no pudo ni empezar. Aquí caben tres
   *     cosas distintas —enlace caducado, CSP, archivo que no es vídeo— y el
   *     código no las separa, así que no se afirma ninguna a la ligera: primero
   *     se mira si la CSP lo dijo, y si no, se pide un enlace nuevo y solo
   *     después se descarta que fuera cosa de la caducidad.
   */
  const fallaVideo = useCallback(() => {
    const el = videoRef.current;
    const codigo = el && el.error ? el.error.code : null;
    const anfitrion = (u) => { try { return u ? new URL(u).host : ''; } catch { return ''; } };
    const donde = anfitrion(urlVideo);

    // Lo primero, lo que no es interpretación mía: si la CSP acaba de bloquear
    // algo, el navegador ya ha dicho qué y por qué directiva.
    const csp = bloqueoCsp.current;
    if (csp && Date.now() - csp.cuando < 10000) {
      const bloqueado = anfitrion(csp.uri) || csp.uri;
      setMotivoVideo(
        `La política de seguridad de la página bloqueó el vídeo: la directiva «${csp.directiva}» ` +
        `no permite ${bloqueado || 'ese origen'}. ` +
        (bloqueado && donde && bloqueado !== donde
          ? `El enlace salía de ${donde} y terminó redirigido ahí. `
          : '') +
        'Se arregla añadiendo ese dominio a media-src en staticwebapp.config.json y volviendo a ' +
        'desplegar; renovar el enlace no cambia nada.'
      );
      setEstadoVideo('error');
      return;
    }

    if (codigo === 2) {
      setMotivoVideo('Se cortó la descarga del vídeo' + (donde ? ` desde ${donde}` : '') + '.');
    } else if (codigo === 4 && !reintentoVideo) {
      // Todavía puede ser un enlace caducado —los de Teams duran cerca de una
      // hora—. Se pide uno nuevo antes de decir nada: dar por caducado lo que
      // no lo está manda a la persona a resolver un problema que no tiene.
      setReintentoVideo(true);
      registrar('El enlace del vídeo no servía; pidiendo uno nuevo');
      renovarVideo();
      return;
    } else if (codigo === 4) {
      // Ya se renovó y sigue igual: caducado no está.
      const archivo = grabacion && grabacion.nombreArchivo;
      setMotivoVideo(
        'El navegador no pudo abrir el archivo' + (donde ? ` en ${donde}` : '') + ', y con un ' +
        'enlace recién pedido tampoco, así que no es que haya caducado. ' +
        (archivo ? `El archivo emparejado es «${archivo}». ` : '') +
        'Mientras tanto se puede ver en Teams.'
      );
    } else if (codigo === 3) {
      setMotivoVideo('El vídeo llegó pero el navegador no pudo decodificarlo.');
    } else {
      setMotivoVideo('El navegador no pudo reproducir este archivo.');
    }

    setEstadoVideo('error');
  }, [urlVideo, reintentoVideo, renovarVideo, grabacion]);

  const irA = (segundos) => {
    if (!videoRef.current || segundos == null) return;
    // Saltar a un minuto con el vídeo plegado reproduciría sonido sin imagen, y
    // quien acaba de pulsar una marca de tiempo quiere VER ese momento. Pedir
    // un minuto es pedir el vídeo.
    setVideoPlegado(false);
    videoRef.current.currentTime = segundos;
    videoRef.current.play().catch(() => { /* el navegador puede exigir un gesto */ });
  };

  // ── Borrar el acta ──────────────────────────────────────────────────────
  //
  // Solo se ofrece para las que se crearon subiendo algo —un texto, un archivo
  // de transcripción o un audio—. El motivo está en `fuenteDeMinuta.js`: esas
  // no tienen reunión detrás, así que son las únicas que quedan atascadas en la
  // lista sin manera de quitarlas, y las únicas en las que «borrar» significa
  // de verdad hacer desaparecer la fila.
  const [borrando, setBorrando] = useState(false);
  const [errorBorrar, setErrorBorrar] = useState(null);

  const eliminarActa = async () => {
    if (!minuta || borrando) return;

    const fuente = datosDeFuente(minuta);
    const titulo = minuta.title || 'esta acta';

    // Se nombra el acta y se dice qué se lleva por delante. Un «¿seguro?» a
    // secas no da la información con la que se decide, y lo que se pierde aquí
    // —el historial de versiones— no es evidente desde esta pantalla.
    if (!await confirmar({
      titulo: 'Borrar el acta',
      mensaje: `Se va a borrar «${titulo}».\n\n`
        + `${fuente.detalle} No hay reunión detrás de la que volver a generarla: `
        + 'el archivo original se quedó en tu equipo.\n\n'
        + 'Se borra también su historial de versiones. Esto no se deshace.',
      aceptar: 'Borrarla',
      peligro: true
    })) {
      return;
    }

    setBorrando(true);
    setErrorBorrar(null);
    try {
      await borrarMinuta(minuta.minuteId, userId);
      // Se vuelve al inicio: quedarse en la pantalla de un acta que ya no
      // existe deja todo el contenido delante y cada botón fallando por su
      // cuenta.
      if (onVolver) onVolver();
    } catch (e) {
      // El caso que importa es `FIRMAS_EN_CURSO`: el backend se niega porque
      // hay personas esperando para firmar. Su mensaje ya dice cuántas y qué
      // hacer, así que se enseña tal cual en vez de traducirlo a «no se pudo».
      setErrorBorrar(e.mensajeUsuario || e.message || 'No se pudo borrar el acta.');
      setBorrando(false);
    }
  };

  const generarInforme = async () => {
    // Regenerar sustituye la minuta que ya hay; no crea otra. El historial de
    // versiones conserva la anterior.
    if (minuta && minuta.html && !await confirmar({
      titulo: 'Esta reunión ya tiene minuta',
      mensaje: 'Volver a generarla la sustituye por una nueva, redactada otra vez desde la ' +
        'transcripción. La actual no se pierde: queda guardada en el historial de versiones.\n\n' +
        'Si solo quieres cambiar algún dato, es mejor «Corregir a mano».',
      aceptar: 'Volver a generarla'
    })) {
      return;
    }

    setGenerando(true);
    setErrorGenerar(null);
    try {
      // Con un formato elegido, la minuta ES ese .docx relleno: se genera el
      // documento y se enseña ese mismo documento. Antes había dos llamadas
      // —una redactaba un informe para la pantalla y otra rellenaba el Word— y
      // salían dos cosas distintas: lo que se revisaba no era lo que se enviaba.
      //
      // Sin formato no hay documento que rellenar, y entonces sí se redacta.
      let resultado;
      if (formatoSel) {
        const relleno = await rellenarWord({
          formatId: Number(formatoSel),
          transcripcion: crudaTranscripcion,
          // La cabecera del acta —fecha, horario, lugar, quién convocó— no está
          // en la transcripción: nadie la dice en voz alta. Sale del calendario.
          cita: datosDeLaCita(reunion, grabacion),
          userId
        });
        // El .docx se guarda tal cual para que descargarlo no vuelva a llamar
        // al modelo: descargar dos veces daría dos documentos distintos.
        //
        // Con el formato dentro: al reabrir la minuta, el selector enseña el
        // formato preferido de la persona, que no tiene por qué ser aquel con
        // el que se hizo ESTA acta. Guardar cambios contra otra plantilla la
        // habría reconstruido sobre un documento que no es el suyo.
        setDocxListo({ ...relleno, formatId: Number(formatoSel) });
        resultado = {
          html: relleno.html,
          verificacion: null,
          // El contenido por zona viaja con la minuta. Es lo que permite rearmar
          // el .docx al reabrirla sin volver a preguntarle al modelo —y sin él,
          // corregir a mano y ver el Word solo existían en la sesión en que se
          // generó el acta.
          meta: { ...(relleno.meta || {}), valores: relleno.valores, regiones: relleno.regiones }
        };
      } else {
        setDocxListo(null);
        resultado = await generarMinuta({
          transcripcion: crudaTranscripcion,
          formatId: null,
          userId
        });
      }

      setMinuta(prev => ({ ...(prev || {}), title: titulo, html: resultado.html, verification: resultado.verificacion }));

      // Se guarda para que la reunión aparezca ya con su informe la próxima vez.
      // Si ya existía, se edita: así no se acumulan minutas de la misma reunión
      // y la anterior queda archivada como versión.
      if (userId && minuta && minuta.minuteId) {
        setGuardando(true);
        try {
          await editarMinuta(minuta.minuteId, userId, {
            html: resultado.html,
            titulo,
            nota: 'Regenerada desde la reunión',
            // Lo mismo que se guarda al crearla. Faltaba aquí, y por eso
            // regenerar un acta ya guardada la dejaba sin zonas ni formato: al
            // reabrirla no había con qué rehacer el .docx y aparecía como texto,
            // sin Word y sin manera de editarla.
            meta: resultado.meta,
            formatId: formatoSel ? Number(formatoSel) : null
          });
        } catch {
          /* el informe recién generado sigue en pantalla */
        } finally {
          setGuardando(false);
        }
      } else if (userId) {
        setGuardando(true);
        try {
          const guardada = await guardarMinuta({
            userId,
            titulo,
            html: resultado.html,
            transcripcion: typeof crudaTranscripcion === 'string'
              ? crudaTranscripcion
              : JSON.stringify(crudaTranscripcion),
            formatId: formatoSel ? Number(formatoSel) : null,
            // La fecha de la reunión, no la de hoy: es la que sitúa la minuta
            // en el listado y la que permite emparejarla con su cita.
            meetingDate: reunion && reunion.inicio
              ? new Date(reunion.inicio).toISOString()
              : (grabacion && grabacion.inicio ? new Date(grabacion.inicio).toISOString() : null),
            reunionId: (reunion && reunion.id) || (grabacion && `grabacion-${grabacion.id}`) || null,
            // Esta pantalla solo genera actas de reuniones: hay una cita o una
            // grabación detrás. Marcarlo es lo que las distingue de las que se
            // crearon subiendo un texto, un archivo o un audio —y lo que evita
            // que se les ofrezca borrar, porque esta sí se puede regenerar.
            fuente: FUENTES.teams.codigo,
            verificacion: resultado.verificacion,
            meta: resultado.meta
          });
          setMinuta(m => ({ ...m, minuteId: guardada.minuteId }));
        } catch {
          // Que no se pueda guardar no invalida el informe recién generado.
        } finally {
          setGuardando(false);
        }
      }
    } catch (e) {
      setErrorGenerar(e.mensajeUsuario || e.message);
    } finally {
      setGenerando(false);
    }
  };

  /**
   * Guarda las zonas corregidas a mano y rehace el Word con ellas.
   *
   * No vuelve a llamar al modelo: se vuelca lo editado sobre la plantilla. Es lo
   * que impide que la corrección se pierda —regenerar devolvería otra acta— y lo
   * que mantiene pegados lo que se ve y lo que se descarga.
   */
  useEffect(() => {
    if (!userId) return;
    let vigente = true;
    listarCatalogo(userId, 'lugar').then(v => { if (vigente) setLugares(v); });
    return () => { vigente = false; };
  }, [userId]);

  /**
   * Dar de alta un lugar sin haberlo escrito antes en un acta.
   *
   * Se pinta en la lista antes de que el servidor conteste: es un nombre que la
   * persona acaba de escribir y esperar medio segundo a verlo aparecer haría
   * dudar de si se guardó. Si el servidor falla, la siguiente visita lo dirá.
   */
  const crearLugar = useCallback(async (valor) => {
    setLugares(l => (l.some(o => o.valor === valor) ? l : [...l, { valor, usos: 1 }]));
    if (userId) await registrarEnCatalogo(userId, 'lugar', valor);
  }, [userId]);

  /**
   * Corregirle el nombre a un lugar.
   *
   * Si el nombre nuevo ya existía, los dos se funden —el servidor suma sus
   * usos—, así que aquí se quita el viejo en vez de renombrarlo en el sitio.
   */
  const renombrarLugar = useCallback(async (antes, despues) => {
    if (userId) await renombrarEnCatalogo(userId, 'lugar', antes, despues);
    setLugares(l => {
      const sinViejo = l.filter(o => o.valor !== antes);
      const usosViejo = (l.find(o => o.valor === antes) || {}).usos || 1;
      const yaEstaba = sinViejo.find(o => o.valor === despues);
      if (yaEstaba) {
        return sinViejo.map(o => (o.valor === despues ? { ...o, usos: o.usos + usosViejo } : o));
      }
      return [...sinViejo, { valor: despues, usos: usosViejo }];
    });
  }, [userId]);

  /**
   * Quitar un lugar de la lista de sugerencias.
   *
   * Hace falta porque el catálogo se llena solo: el día que alguien escribe
   * «Sala de capacitacion» sin tilde, esa variante se queda ofreciéndose para
   * siempre. Se borra del servidor y de la pantalla a la vez; si el servidor
   * falla, vuelve a aparecer en la siguiente visita y no se ha perdido nada.
   */
  const olvidarLugar = useCallback(async (valor) => {
    setLugares(l => l.filter(o => o.valor !== valor));
    if (userId) await olvidarDelCatalogo(userId, 'lugar', valor);
  }, [userId]);

  const guardarZonas = async (valores) => {
    // El formato del documento, no el del selector: son distintos en cuanto se
    // reabre una minuta vieja teniendo otro formato preferido.
    const formato = (docxListo && docxListo.formatId) || (formatoSel ? Number(formatoSel) : null);
    if (!docxListo || !formato) return;

    setGuardandoZonas(true);
    setErrorZonas(null);
    try {
      // Con `minuteId` para que las rubricas ya firmadas vuelvan a estamparse:
      // esto rearma el documento desde la plantilla y una imagen no viaja en
      // los valores.
      const rehecho = await reaplicarWord({
        formatId: formato, valores, userId,
        minuteId: (minuta && minuta.minuteId) || null
      });

      setDocxListo({ ...rehecho, formatId: formato });
      setMinuta(prev => ({ ...(prev || {}), title: titulo, html: rehecho.html }));

      // Queda archivada la versión anterior: si la corrección se hizo mal, el
      // historial la conserva.
      if (userId && minuta && minuta.minuteId) {
        await editarMinuta(minuta.minuteId, userId, {
          html: rehecho.html,
          titulo,
          nota: 'Corregida a mano',
          formatId: formato,
          // Lo dispara el editor al dejar de escribir, no una persona pulsando
          // algo: los seguidos se funden en una sola versión del historial.
          automatico: true,
          // Con las zonas corregidas dentro. Sin esto quedarían guardadas las
          // de antes, y al reabrir el acta se rearmaría con ellas: la
          // corrección se desharía sola y en silencio.
          meta: { ...(rehecho.meta || {}), valores: rehecho.valores, regiones: rehecho.regiones }
        });
      }

      // El lugar que lleva el acta entra al catálogo. Aquí y no al escribirlo:
      // mientras se teclea, cada letra sería un valor nuevo y el catálogo
      // acabaría lleno de «S», «Sa», «Sal». Guardado el acta, lo que hay en esa
      // zona es un lugar de verdad.
      const zonaLugar = zonaDeLugar(docxListo.regiones);
      if (userId && zonaLugar && valores[zonaLugar.id]) {
        const usado = String(valores[zonaLugar.id]).trim();
        if (usado && !lugares.some(l => l.valor === usado)) {
          setLugares(l => [...l, { valor: usado, usos: 1 }]);
        }
        registrarEnCatalogo(userId, 'lugar', usado);
      }
    } catch (e) {
      setErrorZonas(e.mensajeUsuario || e.message || 'No se pudieron guardar los cambios.');
    } finally {
      setGuardandoZonas(false);
    }
  };

  /**
   * Adopta el acta que se acaba de editar en Word.
   *
   * A partir de aquí manda ese documento: el .docx que se descarga, el informe
   * que se ve y las zonas que se editan salen todos de él. Sin esto, editar en
   * Word y luego pulsar «Mi acta en Word» entregaría la versión anterior sin
   * que nadie se enterara, que es el fallo que hace inservible tener dos copias.
   */
  const adoptarDesdeWord = async ({ base64, html, valores, regiones, meta }) => {
    const formato = (docxListo && docxListo.formatId) || (formatoSel ? Number(formatoSel) : null);

    setDocxListo(prev => ({
      // Se conserva lo que no cambia al editar —el nombre del archivo, el tipo—
      // y se sustituye lo que sí: el documento y lo que se lee de él.
      ...(prev || {}),
      base64,
      html,
      valores,
      regiones,
      formatId: formato,
      descargar() {
        const bytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
        const url = URL.createObjectURL(new Blob([bytes], {
          type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        }));
        const a = document.createElement('a');
        a.href = url;
        a.download = (prev && prev.fileName) || `${titulo}.docx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    }));

    setMinuta(prev => ({ ...(prev || {}), title: titulo, html }));
    registrar('Traídos los cambios hechos en Word');

    if (userId && minuta && minuta.minuteId) {
      try {
        await editarMinuta(minuta.minuteId, userId, {
          html,
          titulo,
          nota: 'Editada en Word',
          meta: { ...(meta || {}), valores, regiones },
          formatId: formato
        });
      } catch (e) {
        setErrorGenerar(`Los cambios se ven aquí, pero no se pudieron guardar: ${e.mensajeUsuario || e.message}`);
      }
    }
  };

  const copiarInforme = async () => {
    if (!minuta || !minuta.html) return;
    const texto = textoDeHtml(minuta.html);
    try {
      await navigator.clipboard.writeText(texto);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      setErrorGenerar('El navegador no dejó copiar al portapapeles.');
    }
  };

  // ── Los menús ────────────────────────────────────────────────────────
  //
  // Las acciones las siguen definiendo sus dueños —descargas y navegación en
  // AccionesReunion, publicar en PublicarActa—; aquí solo se decide en qué
  // categoría cae cada una. Por eso ambos son enganches y no componentes: la
  // pantalla es quien sabe que «Exportar» y «Publicar» son las dos cosas que
  // alguien busca bajo «Archivo», aunque vivan en archivos distintos.
  const acciones = useAccionesReunion({
    onPestana: setPestana,
    docxListo,
    cita: datosDeLaCita(reunion, grabacion),
    minuta,
    titulo,
    formatos,
    formatoSel,
    transcripcion: crudaTranscripcion,
    userId,
    documento: documentoEnLinea,
    tokenGraph
  });

  /**
   * A quién proponer como firmante: los asistentes que ya están en el acta.
   *
   * Se leen de lo GUARDADO y no del editor abierto. La diferencia entre los dos
   * es lo que se esté escribiendo en ese momento, y un nombre a medio teclear no
   * emparejaría con nadie del directorio de todos modos.
   */
  const asistentesDelActa = useMemo(() => {
    if (!docxListo || !docxListo.regiones) return [];
    const zona = zonaDeAsistentes(docxListo.regiones);
    if (!zona) return [];
    const filas = (docxListo.valores || {})[zona.region.id];
    if (!Array.isArray(filas)) return [];
    return filas
      .map(f => String((f || [])[zona.columnas.nombre] || '').trim())
      .filter(Boolean);
  }, [docxListo]);

  /**
   * Lo que va a ver quien firme, sin abrir el Word.
   *
   * Se manda con la ronda y se guarda con ella, no se lee de la minuta en vivo:
   * si alguien edita el acta mientras está en firma, quien firme después tiene
   * que ver lo MISMO que vieron los anteriores. Si no, cada firma sería sobre
   * un documento distinto y la ronda no significaría nada.
   *
   * Es un resumen, no el acta: el documento entero está a un clic desde la
   * misma pantalla. Lo que se busca es que nadie firme sin saber qué firma,
   * que es lo que pasa cuando lo único que se enseña es el nombre de un archivo.
   */
  const resumenParaFirmas = useMemo(() => {
    if (!docxListo || !docxListo.regiones) return null;
    const valores = docxListo.valores || {};

    // Los acuerdos, con su responsable y su fecha: es lo que compromete a
    // alguien y por tanto lo que de verdad se está firmando.
    const acuerdos = [];
    for (const zona of zonasDeTareas(docxListo.regiones)) {
      const filas = valores[zona.region.id];
      if (!Array.isArray(filas)) continue;
      for (const fila of filas) {
        const texto = String((fila || [])[zona.columnas.titulo] || '').trim();
        if (!texto) continue;
        acuerdos.push({
          texto,
          responsable: zona.columnas.responsable !== null
            ? String((fila || [])[zona.columnas.responsable] || '').trim() : '',
          fecha: zona.columnas.fecha !== null
            ? String((fila || [])[zona.columnas.fecha] || '').trim() : ''
        });
      }
    }

    // El objetivo, si la plantilla lo tiene. Se busca por la etiqueta y no por
    // una posición fija: cada plantilla ordena sus campos como quiere.
    let objetivo = '';
    for (const region of docxListo.regiones) {
      if (region.tipo === 'lista') continue;
      if (!/objetivo|prop[óo]sito|asunto/i.test(String(region.etiqueta || ''))) continue;
      const v = valores[region.id];
      if (typeof v === 'string' && v.trim()) { objetivo = v.trim(); break; }
    }

    return {
      objetivo: objetivo || null,
      fecha: (reunion && reunion.inicio) || null,
      asistentes: asistentesDelActa,
      acuerdos
    };
  }, [docxListo, asistentesDelActa, reunion]);

  /**
   * Lo que va al extremo derecho de la barra de menús.
   *
   * Antes eran las palancas de vista. Ya no hacen falta: el documento se ve en
   * el panel de la derecha, a la vez que los campos, y no hay nada entre lo que
   * alternar. Queda la acción.
   *
   * Se pinta aquí y no en la barra porque quien sabe cuándo tiene sentido es
   * esta pantalla: hace falta el documento y sus zonas.
   */
  const accionesDeBarra = (docxListo && docxListo.base64 && docxListo.regiones) ? (
    <div style={S.palancas}>
      {/* Llevar los acuerdos a Planner es lo que más se hace con un acta
          terminada, y estaba debajo de la tabla, a una pantalla de distancia
          del resto de acciones. Solo sale con el editor abierto: el diálogo
          lee las filas de ese panel, y con la vista previa delante el panel no
          existe. */}
      {/* Mandar a firmar es lo último que se hace con un acta, y hasta ahora
          no se hacía desde aquí: se bajaba el documento y se mandaba por
          correo a mano. Está al lado de lo de Planner porque las dos son lo
          mismo —sacar el acta de esta pantalla hacia donde trabaja la gente—. */}
      {minuta && minuta.minuteId && (
        <button
          style={S.accionBarra}
          onClick={() => setFirmas(true)}
          title={documentoEnLinea
            ? 'Mandarla a firmar: a cada firmante le llega en PDF a su correo'
            : 'El acta se está subiendo; en cuanto esté se puede mandar'}
        >
          <span aria-hidden="true">✍</span>
          Enviar a firmas
        </button>
      )}

      {edicion && edicion.hayTareas && (
        <button
          style={S.accionBarra}
          onClick={edicion.abrirPlanner}
          title={edicion.tareasHechas > 0
            ? `${edicion.tareasHechas} de los acuerdos ya tienen tarea en Planner`
            : 'Crear una tarea de Planner por cada acuerdo'}
        >
          <span aria-hidden="true">↗</span>
          {edicion.tareasHechas > 0 ? 'Crear en Planner las que faltan' : 'Crear estas tareas en Planner'}
        </button>
      )}

    </div>
  ) : null;

  const publicacion = usePublicarActa({
    base64: (docxListo && docxListo.base64) || null,
    titulo,
    fecha: (reunion && reunion.inicio) || (grabacion && grabacion.inicio) || null,
    tokenGraph
  });

  // Sin `useMemo` a propósito: sus dependencias son funciones que se redefinen
  // en cada pintado, así que el memo se recalcularía igual y solo aparentaría un
  // ahorro. Construir esta lista es armar unos cuantos objetos.
  const menus = (() => {
    const hayInforme = Boolean(minuta && minuta.html);

    const archivo = [
      {
        id: 'exportar',
        etiqueta: 'Exportar',
        icono: '⤓',
        submenu: acciones.elementosDe('Descargar')
      }
    ];

    if (publicacion.disponible) {
      archivo.push({ separador: true });
      archivo.push({
        id: 'publicar',
        icono: '↗',
        etiqueta: publicacion.trabajando ? 'Publicando…' : 'Publicar en SharePoint',
        detalle: `Deja el acta en ${publicacion.destino ? publicacion.destino.sitio : 'la biblioteca del equipo'}`,
        hacer: publicacion.publicar
      });
    }

    archivo.push({ separador: true });
    if (formatos.length === 0) {
      archivo.push({
        id: 'subir-primero',
        icono: '📄',
        etiqueta: 'Subir mi acta en Word',
        detalle: 'Sin plantilla propia la minuta sale como texto redactado.',
        hacer: () => { window.location.hash = '#v2/config'; }
      });
    }
    if (onGenerarConFormato) {
      archivo.push({
        id: 'otro-formato',
        icono: '↥',
        etiqueta: 'Subir otro formato',
        hacer: () => onGenerarConFormato(reunion)
      });
    }

    // Borrar va al final del menú, detrás de un separador y solo cuando se
    // puede. No se pinta en gris para las de Teams: un elemento destructivo
    // permanentemente apagado invita a probar a ver qué pasa, y la pregunta que
    // deja —«¿por qué no puedo borrar esta?»— no la contesta un botón gris.
    if (sePuedeEliminar(minuta)) {
      archivo.push({ separador: true });
      archivo.push({
        id: 'borrar',
        icono: '🗑',
        etiqueta: borrando ? 'Borrando…' : 'Borrar el acta',
        detalle: `${datosDeFuente(minuta).detalle} No se puede deshacer.`,
        peligro: true,
        hacer: eliminarActa
      });
    }

    const lista = [{ nombre: 'Archivo', elementos: archivo }];

    if (hayInforme) {
      lista.push({
        nombre: 'Editar',
        elementos: [
          {
            id: 'regenerar',
            icono: '↻',
            etiqueta: generando ? 'Generando…' : 'Volver a generar',
            detalle: 'Redacta la minuta otra vez desde la transcripción. Gasta tokens.',
            hacer: () => { if (!generando) generarInforme(); }
          },
          {
            id: 'copiar',
            icono: '⧉',
            etiqueta: copiado ? 'Copiado' : 'Copiar el texto',
            detalle: 'El acta como texto plano, para pegarla donde haga falta.',
            hacer: copiarInforme
          }
        ]
      });

      // Deshacer entra en «Editar» solo cuando se está editando: fuera del
      // panel no hay nada que deshacer, y un elemento permanentemente gris
      // enseña a ignorar el menú.
      if (edicion) {
        const editar = lista[lista.length - 1].elementos;
        editar.unshift({ separador: true });
        editar.unshift({
          id: 'rehacer',
          icono: '↷',
          etiqueta: 'Rehacer',
          detalle: 'Ctrl+Shift+Z',
          traba: edicion.puedeRehacer ? null : { texto: 'No hay nada que rehacer' },
          hacer: edicion.rehacer
        });
        editar.unshift({
          id: 'deshacer',
          icono: '↶',
          etiqueta: 'Deshacer',
          detalle: 'Ctrl+Z',
          traba: edicion.puedeDeshacer ? null : { texto: 'No hay nada que deshacer' },
          hacer: edicion.deshacer
        });
      }
    }

    // El menú «Ver» se ha ido: cambiar entre los campos y el documento son ahora
    // dos pestañas encima del acta. Tenerlo en los dos sitios obligaba a
    // mantener sincronizadas dos marcas de lo mismo, y escondía en un menú algo
    // que se usa constantemente mientras se revisa.

    const irA = acciones.elementosDe('Ir a');
    if (irA.length) lista.push({ nombre: 'Ir a', elementos: irA });

    return lista;
  })();

  const enviar = async () => {
    const texto = pregunta.trim();
    if (!texto || preguntando) return;

    const historial = hilo.map(m => ({ role: m.mia ? 'user' : 'assistant', content: m.texto }));
    setHilo(h => [...h, { mia: true, texto }]);
    setPregunta('');
    setPreguntando(true);

    try {
      const { respuesta } = await preguntarSobreReunion({
        pregunta: texto,
        transcripcion: crudaTranscripcion,
        historial,
        minutaHtml: (minuta && minuta.html) || null
      });
      setHilo(h => [...h, { mia: false, texto: respuesta }]);
    } catch (e) {
      setHilo(h => [...h, { mia: false, texto: `No se pudo responder: ${e.mensajeUsuario || e.message}`, error: true }]);
    } finally {
      setPreguntando(false);
    }
  };

  const redactarCorreo = async () => {
    setRedactando(true);
    try {
      const { respuesta } = await preguntarSobreReunion({
        pregunta:
          'Redacta un correo de seguimiento para los asistentes. Empieza por la línea "Asunto: ..." ' +
          'y sigue con el cuerpo: un párrafo de contexto, los acuerdos en viñetas, las tareas con su ' +
          'responsable, y el cierre. Solo con lo que se trató en la reunión.',
        transcripcion: crudaTranscripcion,
        minutaHtml: (minuta && minuta.html) || null
      });
      setCorreoBorrador(respuesta);
    } catch (e) {
      setCorreoBorrador(`No se pudo redactar: ${e.mensajeUsuario || e.message}`);
    } finally {
      setRedactando(false);
    }
  };

  const abrirEnCorreo = () => {
    const lineas = correoBorrador.split('\n');
    const asunto = /^asunto:/i.test(lineas[0]) ? lineas[0].replace(/^asunto:\s*/i, '') : titulo;
    const cuerpo = /^asunto:/i.test(lineas[0]) ? lineas.slice(1).join('\n').trim() : correoBorrador;
    const para = (reunion && reunion.asistentesCorreo) ? reunion.asistentesCorreo.join(';') : '';
    window.location.href =
      `mailto:${encodeURIComponent(para)}?subject=${encodeURIComponent(asunto)}&body=${encodeURIComponent(cuerpo)}`;
  };

  const guardarComentario = () => {
    const texto = comentarioNuevo.trim();
    if (!texto) return;
    const nuevos = [...comentarios, {
      id: Date.now(),
      texto,
      autor: (cuenta && cuenta.name) || 'Yo',
      fecha: new Date().toISOString(),
      segundo: videoRef.current ? Math.round(videoRef.current.currentTime) : null
    }];
    setComentarios(nuevos);
    setComentarioNuevo('');
    try {
      window.localStorage.setItem(claveLocal, JSON.stringify(nuevos));
    } catch {
      /* sin almacenamiento local, el comentario vive solo en esta sesión */
    }
  };

  /**
   * Por qué una pestaña está vacía.
   *
   * Se muestra en todas las que dependen de la transcripción. Un "no hay nada"
   * a secas obliga a adivinar si falló la búsqueda, el permiso o es que la
   * reunión no se transcribió.
   */
  const explicacion = () => {
    if (cargandoMedios) {
      return (
        <Leyendo texto="Buscando la grabación y la transcripción…" color={C.suave} acento={C.acento} />
      );
    }

    return (
      <div style={{ ...S.vacio, textAlign: 'center', margin: '0 auto' }}>
        <p style={{ marginTop: 0 }}>
          {grabacion
            ? 'Se encontró la grabación, pero no su transcripción.'
            : 'No se encontró grabación de esta reunión en tu OneDrive.'}
        </p>
        {/* Aquí iban un aviso en rojo y un párrafo que decían «elige la
            grabación abajo» y «en el desplegable de la derecha». Los dos
            mandaban a un selector de grabaciones que ya no está: eran
            instrucciones para una pantalla anterior, pintadas en rojo de alarma
            sobre algo que no es un error sino un hecho —esta reunión no se
            grabó, o su grabación no se reconoció—. El dibujo y la frase de
            arriba ya lo dicen, y sin sobresaltar.

            Lo que sí sigue apareciendo es un fallo de verdad —Graph que niega
            el permiso, la descarga que revienta—, porque eso no se adivina
            mirando la pantalla. En gris y en pequeño: es información, no una
            alarma. */}
        {errorMedios && (
          <p style={{ fontSize: 12.5, color: C.tenue, margin: '10px 0 0' }}>{errorMedios}</p>
        )}
      </div>
    );
  };

  // ── Pestañas del panel principal ───────────────────────────

  const vistaInforme = () => {
    if (minuta && minuta.html) {
      return (
        <>
          <style>{estiloInforme()}</style>
          {errorGenerar && <div style={aviso('error')}>{errorGenerar}</div>}
          {guardando && <div style={aviso()}>Guardando la minuta…</div>}

          {/* Aquí solo se corrige. El documento —el acta de verdad, la que
              va a salir impresa— se ve en el panel de la derecha, a la vez que
              estos campos y sin alternar entre dos vistas.

              Lo que sigue sin verse junto a los campos es el acta REDACTADA:
              son la misma minuta contada dos veces, y la de arriba se queda
              vieja en cuanto tocas un campo. */}
          {!(docxListo && docxListo.base64) && (
            <>
              {/* Un acta guardada como texto: está el informe, pero no el
                  documento ni la plantilla con la que se hizo, así que no hay
                  nada que abrir en Word. Se dice y se ofrece la única salida
                  real —volver a generarla— en lugar de dejar un hueco mudo
                  donde la persona espera encontrar el editor. */}
              {!puedeVerseEnWord && hayTranscripcion && (
                <div style={S.llamada}>
                  <p style={{ margin: '0 0 14px' }}>
                    Esta minuta se guardó solo como texto: no lleva documento que abrir en Word.
                    Vuelve a generarla con un formato y podrás escribir dentro.
                  </p>
                  <button
                    style={{ ...S.boton, ...S.botonAcento }}
                    onClick={generarInforme}
                    disabled={generando || !formatoSel}
                  >
                    {generando ? 'Generando…' : '✎ Generar minuta'}
                  </button>
                  {!formatoSel && (
                    <p style={{ margin: '12px 0 0', fontSize: 12.5, color: C.tenue }}>
                      No tienes ningún formato por defecto. Elige uno en Configuración: sin
                      plantilla el acta vuelve a salir como texto.
                    </p>
                  )}
                </div>
              )}

              {/* El acta redactada, mientras no haya documento.

                  Esta rama también corre mientras se rehace el documento de una
                  minuta guardada, que tarda un instante. Pintarla sin mirar
                  hacía que el acta blanca apareciera y se fuera sola en cuanto
                  el editor tomaba el relevo. Un parpadeo es peor que una espera:
                  parece que algo se ha roto. */}
              {!rearmando && (
                <div className="minuta-v2" dangerouslySetInnerHTML={{ __html: minuta.html }} />
              )}

              {/* La misma espera que en la pantalla de inicio, con el mismo
                  aro: entrar a una reunión y abrir la lista de reuniones son
                  dos esperas del mismo tipo y no tienen por qué anunciarse de
                  dos maneras distintas. */}
              {rearmando && (
                <Leyendo texto="Revisando reunión…" color={C.suave} acento={C.acento} />
              )}
            </>
          )}

          {/* Solo con formato hay zonas que corregir: sin plantilla el informe
              es prosa redactada y no un documento con huecos. */}
          {docxListo && docxListo.regiones && (
            <ZonasDelActa
              regiones={docxListo.regiones}
              // El folio lo pone la base de datos al guardar, no el modelo: lo
              // que venga en esa zona del documento se sustituye por el que
              // lleva esta minuta, y de ahí baja al .docx en el siguiente
              // volcado como un valor más.
              valores={conFolio(docxListo.valores || {}, docxListo.regiones, minuta && minuta.folio)}
              folio={(minuta && minuta.folio) || null}
              catalogoLugares={lugares}
              onCrearLugar={crearLugar}
              onRenombrarLugar={renombrarLugar}
              onOlvidarLugar={olvidarLugar}
              sinDato={docxListo.sinDato || []}
              onGuardar={guardarZonas}
              guardando={guardandoZonas}
              error={errorZonas}
              fechaReunion={(reunion && reunion.inicio) || (grabacion && grabacion.inicio) || null}
              tokenGraph={tokenGraph}
              titulo={titulo}
              // Para saber qué tareas mandó ya esta acta a Planner.
              minuteId={(minuta && minuta.minuteId) || null}
              userId={userId}
              onEstado={setEdicion}
            />
          )}
        </>
      );
    }

    if (!hayTranscripcion) {
      return (
        // Centrado en los dos ejes. Pegado arriba, el dibujo dejaba media
        // pantalla de vacío debajo y parecía que faltaba por cargar algo.
        <div
          style={{
            ...S.vacio, textAlign: 'center', margin: '0 auto',
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            minHeight: 'calc(100vh - 260px)'
          }}
        >
          {errorGenerar && <div style={aviso('error')}>{errorGenerar}</div>}
          {/* ── Sin dibujo, y a proposito ────────────────────────────
              Aqui habia una ilustracion de carpeta con una X ROJA. Que no
              haya transcripcion no es un error: es un estado normal de una
              reunion que aun no se ha transcrito, y muy frecuente mientras
              la minuta termina de cargar.

              Una X roja a media pantalla lo convierte en una averia a la
              vista, y manda a buscar un fallo donde no lo hay. El texto de
              abajo explica lo que pasa y que hacer, que es lo que hacia
              falta. */}
          <p style={{ marginTop: 0 }}>
            El informe se redacta a partir de lo que se dijo: sin transcripción no hay de dónde
            sacarlo.
          </p>
          {explicacion()}
        </div>
      );
    }

    // Sin acta todavía. Un solo botón, en el centro y con el nombre de lo que
    // hace: es la única acción posible en esta pantalla, y esconderla al final
    // de un párrafo obligaba a leer para encontrar lo evidente.
    return (
      <>
        {errorGenerar && <div style={aviso('error')}>{errorGenerar}</div>}
        <div style={S.llamada}>
          <button
            style={{ ...S.boton, ...S.botonAcento }}
            onClick={generarInforme}
            disabled={generando}
          >
            {generando ? 'Generando…' : '✎ Generar minuta'}
          </button>
          <p style={{ margin: '14px 0 0', fontSize: 12.5, color: C.tenue }}>
            {trans.turnos.length || 1} intervenciones, {totalPalabras.toLocaleString('es')} palabras.
            {formatoSel
              ? ' Se rellenará tu formato por defecto y podrás editarla en Word.'
              : ' Marca un formato por defecto en Configuración si quieres poder editarla en Word.'}
          </p>
        </div>
      </>
    );
  };

  const vistaTranscripcion = () => {
    if (!hayTranscripcion) return explicacion();

    if (trans.turnos.length === 0) {
      return <div style={{ ...S.vacio, whiteSpace: 'pre-wrap' }}>{trans.texto}</div>;
    }

    return (
      <>
        <input
          style={{ ...S.campoTexto, marginBottom: 14 }}
          placeholder="Buscar en la transcripción…"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
        />

        {busqueda && (
          <div style={{ fontSize: 12.5, color: C.tenue, marginBottom: 10 }}>
            {turnosVisibles.length} de {trans.turnos.length} intervenciones
          </div>
        )}

        {turnosVisibles.map((turno, i) => (
          <div key={i} style={S.turno}>
            <button
              style={S.sello}
              onClick={() => irA(turno.inicio)}
              title={grabacion ? 'Ir a este momento del vídeo' : 'Sin vídeo vinculado'}
            >
              {turno.inicio != null ? comoReloj(turno.inicio) : '—'}
            </button>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 3, color: colorDe(turno.hablante || '?') }}>
                {turno.hablante || 'Sin identificar'}
              </div>
              <div style={{ fontSize: 14, lineHeight: 1.65 }}>{turno.texto}</div>
            </div>
          </div>
        ))}
      </>
    );
  };

  const vistaClips = () => {
    if (!hayTranscripcion) {
      return explicacion();
    }

    return (
      <>
        {momentos.length === 0 && (
          <div style={S.vacio}>No se detectaron acuerdos, decisiones ni pendientes explícitos.</div>
        )}

        {momentos.map((m, i) => (
          <div
            key={i}
            style={{ ...S.tarjetaHablante, cursor: 'pointer' }}
            onClick={() => irA(m.turno.inicio)}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 7 }}>
              <span style={{
                fontSize: 11.5, padding: '2px 8px', borderRadius: 20,
                background: `${m.color}22`, color: m.color, border: `1px solid ${m.color}55`
              }}>
                {m.tipo}
              </span>
              <span style={{ fontSize: 12, color: C.tenue, fontVariantNumeric: 'tabular-nums' }}>
                {m.turno.inicio != null ? comoReloj(m.turno.inicio) : '—'}
              </span>
              <span style={{ fontSize: 12.5, color: C.suave }}>{m.turno.hablante || 'Sin identificar'}</span>
            </div>
            <div style={{ fontSize: 13.5, lineHeight: 1.6 }}>
              {m.turno.texto.length > 320 ? `${m.turno.texto.slice(0, 320)}…` : m.turno.texto}
            </div>
          </div>
        ))}
      </>
    );
  };

  const vistaCuadro = () => {
    if (!hayTranscripcion) {
      return explicacion();
    }

    const metrica = (valor, etiqueta) => (
      <div style={S.metrica} key={etiqueta}>
        <div style={S.metricaValor}>{valor}</div>
        <div style={S.metricaEtiqueta}>{etiqueta}</div>
      </div>
    );

    return (
      <>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 13, marginBottom: 30 }}>
          {metrica(duracion ? comoReloj(duracion) : '—', 'Duración')}
          {metrica(hablantes.length, 'Participantes')}
          {metrica(totalPalabras.toLocaleString('es'), 'Palabras')}
          {metrica(trans.turnos.length, 'Intervenciones')}
          {metrica(momentos.length, 'Momentos marcados')}
        </div>

        <h3 style={{ fontSize: 15.5, fontWeight: 600, margin: '0 0 14px' }}>Reparto de la palabra</h3>
        {hablantes.map(h => (
          <div key={h.nombre} style={{ marginBottom: 15 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13.5, marginBottom: 6 }}>
              <span>{h.nombre}</span>
              <span style={{ color: C.suave, fontVariantNumeric: 'tabular-nums' }}>
                {h.porcentaje}% · {comoReloj(h.segundos)} · {h.intervenciones} turnos
              </span>
            </div>
            <div style={{ height: 8, background: C.panelAlto, borderRadius: 4, overflow: 'hidden' }}>
              <div style={{ width: `${h.porcentaje}%`, height: '100%', background: colorDe(h.nombre) }} />
            </div>
          </div>
        ))}
      </>
    );
  };

  const vistaEmail = () => (
    <>
      <p style={{ ...S.vacio, marginTop: 0 }}>
        Correo de seguimiento para los asistentes, redactado con lo que se trató en la reunión.
      </p>

      <div style={{ display: 'flex', gap: 9, marginBottom: 14, flexWrap: 'wrap' }}>
        <button
          style={{ ...S.boton, ...S.botonAcento }}
          onClick={redactarCorreo}
          disabled={redactando || !hayTranscripcion}
        >
          {redactando ? 'Redactando…' : correoBorrador ? 'Volver a redactar' : 'Redactar con IA'}
        </button>
        {correoBorrador && (
          <>
            <button style={S.boton} onClick={abrirEnCorreo}>Abrir en el correo</button>
            <button
              style={S.boton}
              onClick={() => navigator.clipboard.writeText(correoBorrador).catch(() => {})}
            >
              Copiar
            </button>
          </>
        )}
      </div>

      {!hayTranscripcion && <div style={aviso()}>Sin transcripción no hay nada que redactar.</div>}

      <textarea
        style={{ ...S.campoTexto, minHeight: 380 }}
        value={correoBorrador}
        onChange={(e) => setCorreoBorrador(e.target.value)}
        placeholder="El borrador aparecerá aquí, y puedes editarlo antes de enviarlo."
      />
    </>
  );

  const vistaPreguntar = () => (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 11, paddingBottom: 14 }}>
        {hilo.length === 0 && (
          <div style={S.vacio}>
            <p style={{ marginTop: 0 }}>Pregunta lo que quieras sobre esta reunión.</p>
            <p style={{ fontSize: 13 }}>
              Las respuestas salen únicamente de la transcripción: si algo no se dijo, te lo dirá en
              vez de rellenarlo.
            </p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 14 }}>
              {['¿Qué acuerdos quedaron?', '¿Quién se lleva cada tarea?', '¿Quedó alguna fecha?'].map(s => (
                <button key={s} style={S.boton} onClick={() => setPregunta(s)}>{s}</button>
              ))}
            </div>
          </div>
        )}

        {hilo.map((m, i) => (
          <div
            key={i}
            style={{ ...S.burbuja(m.mia), ...(m.error ? aviso('error') : {}) }}
          >
            {m.texto}
          </div>
        ))}

        {preguntando && <div style={S.burbuja(false)}>Buscando en la transcripción…</div>}
      </div>

      <div style={{ display: 'flex', gap: 9, paddingTop: 12, borderTop: `1px solid ${C.borde}` }}>
        <input
          style={{ ...S.campoTexto, flex: 1 }}
          placeholder={hayTranscripcion ? 'Escribe tu pregunta…' : 'Sin transcripción no hay nada que preguntar'}
          value={pregunta}
          disabled={!hayTranscripcion}
          onChange={(e) => setPregunta(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); enviar(); } }}
        />
        <button
          style={{ ...S.boton, ...S.botonAcento }}
          onClick={enviar}
          disabled={preguntando || !pregunta.trim() || !hayTranscripcion}
        >
          Preguntar
        </button>
      </div>
    </div>
  );

  // ── Panel derecho ──────────────────────────────────────────

  const vistaHablantes = () => {
    if (hablantes.length === 0) {
      // Aquí no se repite el diagnóstico completo: el selector y el aviso ya
      // están justo encima, bajo el reproductor.
      return (
        <div style={S.vacio}>
          {cargandoMedios
            ? 'Buscando la transcripción…'
            : 'Sin transcripción no se sabe quién habló ni cuánto.'}
        </div>
      );
    }

    return hablantes.map(h => (
      <div key={h.nombre} style={S.tarjetaHablante}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
          <span style={S.avatar(colorDe(h.nombre))}>{h.inicial}</span>
          <span style={{ flex: 1, fontSize: 13.5, fontWeight: 600, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {h.nombre}
          </span>
          <span
            style={{ fontSize: 12.5, color: C.suave, fontVariantNumeric: 'tabular-nums' }}
            title="Palabras por minuto sobre el tiempo que esta persona habló: mide el ritmo, no cuánto participó."
          >
            PPM: {h.ppm} ⓘ
          </span>
          <button
            style={S.iconoBoton}
            onClick={() => setExpandido(e => ({ ...e, [h.nombre]: !e[h.nombre] }))}
          >
            {expandido[h.nombre] ? '⌃' : '⌄'}
          </button>
        </div>

        <div style={S.linea}>
          {h.marcas.map((marca, i) => (
            <div
              key={i}
              style={{
                position: 'absolute', left: `${marca.izquierda}%`, width: `${marca.ancho}%`,
                top: 0, bottom: 0, background: colorDe(h.nombre), borderRadius: 2
              }}
            />
          ))}
        </div>

        {expandido[h.nombre] && (
          <div style={{ fontSize: 12.5, color: C.suave, marginTop: 11, lineHeight: 1.8 }}>
            <div>Tiempo hablado: {comoReloj(h.segundos)} ({h.porcentaje}% del total)</div>
            <div>Intervenciones: {h.intervenciones}</div>
            <div>Palabras: {h.palabras.toLocaleString('es')}</div>
          </div>
        )}
      </div>
    ));
  };

  const vistaInformacion = () => {
    const filas = [
      ['Título', titulo],
      ['Fecha', reunion && reunion.inicio
        ? new Date(reunion.inicio).toLocaleString('es', { dateStyle: 'full', timeStyle: 'short' })
        : (minuta && minuta.meetingDate ? new Date(minuta.meetingDate).toLocaleString('es') : null)],
      ['Duración', duracion ? comoReloj(duracion) : null],
      ['Organizador', reunion && reunion.organizador],
      ['Origen', reunion && reunion.soloGrabacion
        ? 'Grabación de Teams (sin cita en el calendario)'
        : (reunion ? 'Cita del calendario' : null)],
      ['Lugar', reunion && (reunion.ubicacion || (reunion.esEnLinea ? 'Microsoft Teams' : null))],
      // Uno por renglón, no separados por comas. Catorce nombres compuestos en
      // un párrafo corrido son cinco líneas donde los apellidos de uno y el
      // nombre del siguiente se tocan: para saber quién estaba hay que leerlo
      // entero. En lista se cuentan de un vistazo.
      ['Asistentes', reunion && reunion.asistentes && reunion.asistentes.length > 0
        ? (
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {reunion.asistentes.map((quien, i) => (
              <li key={i} style={{ marginBottom: 2 }}>{quien}</li>
            ))}
          </ul>
        )
        : null],
      ['Grabación', grabacion
        ? `${grabacion.titulo}${grabacion.motivo ? ` — ${grabacion.motivo}` : ''}`
        : (cargandoMedios ? 'Buscando…' : 'No vinculada')],
      ['Archivo', grabacion && grabacion.nombreArchivo],
      ['Transcripción', hayTranscripcion
        ? `${trans.turnos.length} intervenciones · ${totalPalabras.toLocaleString('es')} palabras`
        : 'No disponible'],
      ['Minuta', minuta && minuta.minuteId ? `Guardada (#${minuta.minuteId})` : 'Sin guardar']
    ].filter(([, valor]) => valor);

    return (
      <div style={{ fontSize: 13.5, lineHeight: 1.7 }}>
        {filas.map(([etiqueta, valor]) => (
          <div key={etiqueta} style={{ padding: '9px 0', borderBottom: `1px solid ${C.panelAlto}` }}>
            <div style={{ fontSize: 12, color: C.tenue, marginBottom: 2 }}>{etiqueta}</div>
            <div>{valor}</div>
          </div>
        ))}

        {reunion && reunion.enlaceOutlook && (
          <a
            href={reunion.enlaceOutlook} target="_blank" rel="noreferrer"
            style={{ ...S.boton, marginTop: 16, textDecoration: 'none', display: 'inline-flex' }}
          >
            Abrir la cita en Outlook
          </a>
        )}
      </div>
    );
  };

  const vistaComentarios = () => (
    <>
      <div style={{ fontSize: 12.5, color: C.tenue, marginBottom: 12, lineHeight: 1.6 }}>
        Las notas se guardan en este navegador. No se comparten con el resto de asistentes.
      </div>

      {comentarios.map(c => (
        <div key={c.id} style={S.tarjetaHablante}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: C.tenue, marginBottom: 6 }}>
            <span>{c.autor}</span>
            <span>
              {c.segundo != null && (
                <button style={{ ...S.sello, marginRight: 8 }} onClick={() => irA(c.segundo)}>
                  {comoReloj(c.segundo)}
                </button>
              )}
              {new Date(c.fecha).toLocaleDateString('es')}
            </span>
          </div>
          <div style={{ fontSize: 13.5, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{c.texto}</div>
        </div>
      ))}

      <textarea
        style={{ ...S.campoTexto, minHeight: 80, marginTop: 8 }}
        placeholder="Escribe una nota…"
        value={comentarioNuevo}
        onChange={(e) => setComentarioNuevo(e.target.value)}
      />
      <button
        style={{ ...S.boton, ...S.botonAcento, marginTop: 9 }}
        onClick={guardarComentario}
        disabled={!comentarioNuevo.trim()}
      >
        Añadir nota
      </button>
    </>
  );

  // ── Composición ────────────────────────────────────────────

  // Sin saber de qué reunión se trata no hay nada que enseñar, y decir «no se
  // encontró grabación en tu OneDrive» sería mentir: no se ha llegado a buscar
  // ninguna. Pasa cuando se entra con la dirección a pelo, o al recargar sin
  // que se pueda recuperar lo que se estaba mirando.
  //
  // Va después de todos los hooks y no antes: salir arriba cambiaría cuántos se
  // ejecutan entre una pintada y otra, que es lo que React no permite.
  if (!reunion && minuteId == null) {
    return (
      <div style={S.raiz}>
        <div style={S.barra}>
          <button style={S.volver} onClick={onVolver}>Volver</button>
        </div>
        <div
          style={{
            ...S.vacio, textAlign: 'center', margin: '0 auto',
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            minHeight: 'calc(100vh - 260px)'
          }}
        >
          <p style={{ marginTop: 0 }}>
            Esta dirección no dice qué reunión abrir, así que no hay nada que mostrar.
          </p>
          <p style={{ margin: '6px 0 16px', fontSize: 12.5, color: C.tenue }}>
            Suele pasar al recargar la página. Ábrela desde la agenda y volverás a lo mismo.
          </p>
          <button style={{ ...S.boton, ...S.botonAcento }} onClick={onVolver}>
            Ir a la agenda
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={S.raiz}>
      {/* `scrollbar-width` lo entienden Firefox y poco más; en Chrome y Edge
          —que es donde se usa esto— la barra solo se esconde con el
          pseudoelemento, y un pseudoelemento no cabe en un estilo en línea. */}
      <style>{`.pestanas-v2::-webkit-scrollbar { display: none; }`}</style>

      <div style={S.barra}>
        {/* Un ángulo dibujado, no el carácter «←»: la flecha de texto cambia de
            grosor y de largo según la fuente que tenga el sistema, y en esta
            pantalla salía como una raya larga que no parecía un botón. */}
        <button style={S.volver} onClick={onVolver} title="Volver" aria-label="Volver">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M15 5l-7 7 7 7"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <h1 style={S.titulo}>{titulo}</h1>
        <span style={S.pastilla}>🔒 Privada</span>
        {grabacion && grabacion.seguro === false && (
          <span style={{ ...S.pastilla, color: C.atencion, borderColor: C.atencion }}>
            grabación probable: {grabacion.motivo}
          </span>
        )}

      </div>

      <div style={S.pestanas} className="pestanas-v2">
        {PESTANAS.map(p => (
          <button
            key={p.id}
            style={S.pestana(pestana === p.id, p.destacada)}
            onClick={() => setPestana(p.id)}
          >
            {p.etiqueta}{p.destacada ? ' ★' : ''}
          </button>
        ))}
      </div>

      <div style={S.cuerpo} ref={cuerpoRef}>
        <div style={S.izquierda}>
          {pestana === 'informe' && (
            <div style={S.herramientas}>
              {/* El formato ya no se elige aquí.

                  Era un desplegable con el nombre completo de la plantilla
                  —«Formato minuta checkpoint 01092026 (0…»— presidiendo la
                  barra de todas las reuniones, para una decisión que se toma
                  una vez al año. Y era una decisión peligrosa de tener a mano:
                  cambiarlo volvía a volcar el acta sobre otra plantilla.

                  Vive en Configuración, junto a los formatos, marcado «por
                  defecto». Ahí se ve al lado de los demás y se cambia sabiendo
                  qué se cambia. */}

              {/* Los menús. Antes esto eran once botones en dos filas, todos
                  gritando igual de fuerte: encontrar «PDF» costaba lo mismo que
                  encontrar «Editar la minuta», que es lo que se hace siempre.
                  Agrupado por categorías, lo raro deja de ocupar sitio pero
                  sigue estando donde uno iría a buscarlo. */}
              <BarraDeMenus menus={menus} derecha={accionesDeBarra} />

              {/* Aquí estaba «✎ Editar la minuta / ✓ Terminar de editar».

                  Se ha ido porque ya no significaba nada: los campos son la
                  vista normal del acta y el guardado es automático, así que no
                  hay un modo edición del que entrar y salir ni nada que
                  «terminar». Un botón que dice que has acabado, cuando no
                  había empezado nada, solo hace dudar de si lo escrito se
                  guardó. Cambiar de vista está en el menú «Ver». */}

              {/* Ya no hace falta un separador elástico aquí: la barra de
                  menus ocupa la fila entera y empuja ella lo que venga
                  despues. */}

              {/* Los recados de lo que se acaba de hacer. Van fuera de los menús
                  porque el menú se cierra al pulsar y el mensaje tiene que
                  quedarse a la vista. */}
              {publicacion.dialogo}
              <RecadoDeAcciones recado={acciones.recado} irAConfig={acciones.irAConfig} />
              <RecadoDePublicacion recado={publicacion.recado} />

              {/* Un borrado que no se pudo hacer. El caso real es el acta que
                  está en firma: el mensaje del backend dice cuántas personas
                  faltan y qué hacer, así que se enseña tal cual. Se queda hasta
                  que se cierra: desaparecer solo dejaría a alguien creyendo que
                  el acta se borró. */}
              {errorBorrar && (
                <div
                  style={{ ...aviso('error'), display: 'flex', alignItems: 'flex-start', gap: 10, width: '100%' }}
                  role="alert"
                >
                  <span style={{ flex: 1 }}>{errorBorrar}</span>
                  <button
                    style={{
                      background: 'transparent', border: 'none', cursor: 'pointer',
                      color: 'inherit', fontSize: 17, lineHeight: 1, padding: 0,
                      fontFamily: FUENTE
                    }}
                    onClick={() => setErrorBorrar(null)}
                    aria-label="Cerrar el aviso"
                  >
                    ×
                  </button>
                </div>
              )}
            </div>
          )}

          <div style={{ ...S.contenido, ...(pestana === 'preguntar' ? { display: 'flex', flexDirection: 'column' } : {}) }}>
            {cargandoMedios && pestana !== 'informe' && (
              <div style={{ color: C.suave, fontSize: 14, marginBottom: 14 }}>
                Buscando la grabación y la transcripción…
              </div>
            )}

            {pestana === 'informe' && vistaInforme()}
            {pestana === 'transcripcion' && vistaTranscripcion()}
            {pestana === 'clips' && vistaClips()}
            {pestana === 'cuadro' && vistaCuadro()}
            {pestana === 'email' && vistaEmail()}
            {pestana === 'preguntar' && vistaPreguntar()}
          </div>
        </div>

        {/* La muesca. Arrastrar cambia el reparto; doble clic pliega el panel
            y lo devuelve. Con el teclado, las flechas: quien no usa ratón
            también trabaja en pantallas estrechas. */}
        <div
          style={S.muesca(ajustando)}
          role="separator"
          aria-orientation="vertical"
          aria-label="Ancho del panel derecho"
          aria-valuenow={panelPlegado ? 0 : (anchoPanel || 0)}
          tabIndex={0}
          title={panelPlegado
            ? 'Doble clic para volver a abrir el panel'
            : 'Arrastra para cambiar el ancho. Doble clic para plegarlo.'}
          onMouseDown={(e) => {
            e.preventDefault();
            if (panelPlegado) return;
            setAjustando(true);
          }}
          onDoubleClick={() => setPanelPlegado(p => !p)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
              e.preventDefault();
              const caja = cuerpoRef.current && cuerpoRef.current.getBoundingClientRect();
              if (!caja) return;
              const ahora = panelPlegado ? MIN_PANEL : (anchoPanel || Math.round(caja.width * 0.38));
              const paso = e.key === 'ArrowLeft' ? 24 : -24;
              setPanelPlegado(false);
              anotarAncho(Math.max(MIN_PANEL,
                Math.min(ahora + paso, Math.round(caja.width) - MIN_IZQUIERDA)));
            }
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setPanelPlegado(p => !p);
            }
          }}
        >
          <span style={S.asaMuesca} aria-hidden="true">{panelPlegado ? '‹' : '⋮'}</span>
        </div>

        {/* Mientras se arrastra, una lámina por encima de todo: si no, el
            recuadro de Word se queda con el ratón y el arrastre se muere a
            medio camino. */}
        {ajustando && <div style={S.lamina} />}

        <aside
          style={{
            ...S.derecha,
            // El ancho de por sí solo manda mientras nadie lo haya tocado.
            ...(anchoPanel ? null : (panel === 'documento' ? S.derechaAncha : null)),
            ...(anchoPanel
              // Se encoge si hace falta —«0 1»— y no se estira. Con «0 0», al
              // achicar la ventana el panel conservaba su medida y el editor se
              // quedaba sin sitio hasta desaparecer.
              ? { flex: `0 1 ${anchoPanel}px`, minWidth: 0, maxWidth: 'none' }
              : null),
            ...(panelPlegado ? { display: 'none' } : null)
          }}
        >
          {urlVideo ? (
            <>
              {/* La barra del vídeo. Existe para poder quitarlo de en medio: un
                  16:9 se come media columna, y debajo va el acta. */}
              <button
                type="button"
                style={S.barraVideo}
                onClick={() => setVideoPlegado(v => !v)}
                aria-expanded={!videoPlegado}
                title={videoPlegado
                  ? 'Mostrar la grabación'
                  : 'Plegar la grabación para ver el acta entera'}
              >
                <span aria-hidden="true" style={S.flechaVideo(videoPlegado)}>▾</span>
                <span style={{ flex: 1, textAlign: 'left' }}>Grabación</span>
                <span style={S.pistaVideo}>
                  {videoPlegado ? 'Mostrar' : 'Plegar'}
                </span>
              </button>

              {/* Se esconde, no se quita. Desmontarlo soltaría el archivo: al
                  volver a abrirlo, un vídeo de Teams son cientos de megas que
                  el navegador tendría que empezar a pedir otra vez, y se
                  perdería el minuto en el que se quedó. */}
              <video
                ref={videoRef}
                style={{ ...S.video, ...(videoPlegado ? { display: 'none' } : null) }}
                src={urlVideo}
                controls
                playsInline
                // `metadata` y no `auto`: basta con la duración y el primer
                // fotograma. Con `auto` el navegador se trae el archivo entero
                // —cientos de megas— antes de dejar pulsar play.
                preload="metadata"
                onLoadStart={() => setEstadoVideo('cargando')}
                onLoadedMetadata={() => setEstadoVideo('listo')}
                onCanPlay={() => setEstadoVideo('listo')}
                onStalled={() => setEstadoVideo('atascado')}
                onWaiting={() => setEstadoVideo('esperando')}
                onError={fallaVideo}
              />

              {estadoVideo !== 'listo' && !videoPlegado && (
                <div style={{ padding: '10px 16px', fontSize: 12.5, color: C.suave, lineHeight: 1.6 }}>
                  {estadoVideo === 'error'
                    ? (motivoVideo || 'El navegador no pudo reproducir este archivo.')
                    : estadoVideo === 'atascado'
                      ? 'La descarga se ha detenido. Puede ser la red, o que el enlace haya caducado.'
                      : 'Cargando el vídeo… los de Teams pesan bastante y tarda un poco.'}
                  {grabacion && grabacion.urlWeb && (
                    <>
                      {' '}
                      <a href={grabacion.urlWeb} target="_blank" rel="noreferrer" style={{ color: C.acentoClaro }}>
                        Verlo en Teams
                      </a>
                      {' · '}
                      <button
                        onClick={renovarVideo}
                        style={{
                          background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                          color: C.acentoClaro, fontSize: 12.5, fontFamily: FUENTE
                        }}
                      >
                        Renovar el enlace
                      </button>
                    </>
                  )}
                </div>
              )}
            </>
          ) : cargandoMedios ? (
            <div style={S.videoVacio}><span>Buscando la grabación…</span></div>
          ) : grabacion ? (
            /* Hay grabación pero no se pudo sacar un enlace reproducible. No se
               reserva el hueco entero del reproductor —un rectángulo negro de
               media pantalla para decir que no hay vídeo—, pero sí queda la vía
               de salida: reintentar, o verlo en Teams, que sí funciona. */
            <div style={{
              display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap',
              padding: '10px 16px', fontSize: 12.5, color: C.suave,
              borderBottom: `1px solid ${C.borde}`
            }}>
              <span style={{ flex: 1, minWidth: 180 }}>
                {motivoVideo || 'Se encontró la grabación, pero no se pudo obtener un enlace para reproducirla aquí.'}
              </span>
              <button
                onClick={renovarVideo}
                style={{
                  background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                  color: C.acentoClaro, fontSize: 12.5, fontFamily: FUENTE
                }}
              >
                Reintentar
              </button>
              {grabacion.urlWeb && (
                <a href={grabacion.urlWeb} target="_blank" rel="noreferrer" style={{ color: C.acentoClaro }}>
                  Verlo en Teams
                </a>
              )}
            </div>
          ) : null /* Sin grabación no se pinta nada: el rectángulo negro con el
                      «no se pudo emparejar» ocupaba la mitad del panel para no
                      decir nada que no esté ya en la pestaña Información, y
                      empujaba hacia abajo lo único aprovechable de esta columna.
                      Los paneles suben y ocupan la altura entera. */}

          <div style={{ display: 'flex', gap: 4, padding: '10px 16px 0', borderBottom: `1px solid ${C.borde}` }}>
            {PANELES.map(p => (
              <button key={p.id} style={S.pestana(panel === p.id, false)} onClick={() => setPanel(p.id)}>
                {p.etiqueta}
              </button>
            ))}
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
            {/* El documento no se desmonta al cambiar de pestaña: es quien sube
                el acta a la biblioteca, y hacerlo otra vez cada vez que alguien
                mira los hablantes sería volver a pedirle a OneDrive lo que ya
                está subido. Se esconde, no se quita. */}
            {docxListo && docxListo.base64 && (
              <div style={{ display: panel === 'documento' ? 'block' : 'none' }}>
                <VistaWord
                  activa={panel === 'documento'}
                  base64={docxListo.base64}
                  titulo={titulo}
                  fecha={(reunion && reunion.inicio) || (grabacion && grabacion.inicio) || null}
                  tokenGraph={tokenGraph}
                  onCambios={adoptarDesdeWord}
                  onDocumento={setDocumentoEnLinea}
                />
              </div>
            )}
            {panel === 'documento' && !(docxListo && docxListo.base64) && (
              <p style={{ fontSize: 12.5, color: C.tenue, lineHeight: 1.6 }}>
                Esta minuta no lleva documento: se guardó solo como texto. Vuelve a
                generarla con un formato y aquí se verá como va a salir impresa.
              </p>
            )}
            {panel === 'hablantes' && vistaHablantes()}
            {panel === 'informacion' && vistaInformacion()}
            {panel === 'comentarios' && vistaComentarios()}
          </div>
        </aside>
      </div>

      {firmas && minuta && minuta.minuteId && (
        <EnviarAFirmas
          minuteId={minuta.minuteId}
          userId={userId}
          tokenGraph={tokenGraph}
          titulo={titulo}
          folio={(minuta && minuta.folio) || null}
          miCorreo={miCorreo}
          miNombre={miNombre}
          resumen={resumenParaFirmas}
          documento={documentoEnLinea}
          sugeridos={asistentesDelActa}
          onCerrar={(mandada) => {
            setFirmas(false);
            if (mandada) registrar('acta mandada a firmar');
          }}
        />
      )}

      {dialogo}
    </div>
  );
};

export default DetalleReunion;
